import React, { useState, useRef, useEffect, useMemo, useContext } from "react";
import { jsPDF } from "jspdf";
import PlatformCtx from "../../../state/PlatformCtx";
import { TODAY, fmtSAR, fmtCompact } from "../../../utils/format";
import { locName, localName } from "../../../utils/helpers";
import { fetchForexRate } from "../../../utils/forex";
import AIPanel from "../../../shared/AIPanel";

function generateReportPDF({title,subtitle,state,sections,lang}){
  try{
  const doc=new jsPDF({orientation:"portrait",unit:"mm",format:"a4"});
  const W=210,H=297,M=16,CW=W-M*2;
  const accent=state.branding?.anchorColor||"#C9A84C";
  const hexToRgb=(hex)=>{const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16);return[r,g,b];};
  const [aR,aG,aB]=hexToRgb(accent);
  const appName=state.appName||"Command";
  const tagline=state.tagline||"";
  const legal=state.legalPages||{};
  const companyName=legal.companyName||appName;
  const companyAddr=legal.companyAddress||"";
  const companyEmail=legal.contactEmail||"";
  const cur=state.currency||"SAR";
  const logo=state.branding?.logoLight||state.branding?.logoDark||null;
  let y=0;

  /* ── Header bar ── */
  doc.setFillColor(aR,aG,aB);
  doc.rect(0,0,W,32,"F");
  let logoX=M;
  if(logo){try{doc.addImage(logo,"PNG",M,6,20,20);logoX=M+24;}catch(e){}}
  doc.setTextColor(255,255,255);
  doc.setFontSize(18);doc.setFont("helvetica","bold");
  doc.text(appName,logoX,16);
  if(tagline){doc.setFontSize(9);doc.setFont("helvetica","normal");doc.text(tagline,logoX,22);}
  /* Right side: date */
  doc.setFontSize(8);doc.setFont("helvetica","normal");
  doc.text(new Date().toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"}),W-M,16,{align:"right"});
  y=38;

  /* ── Report title ── */
  doc.setTextColor(40,40,40);
  doc.setFontSize(16);doc.setFont("helvetica","bold");
  doc.text(title,M,y);y+=6;
  if(subtitle){doc.setFontSize(10);doc.setFont("helvetica","normal");doc.setTextColor(120,120,120);doc.text(subtitle,M,y);y+=4;}
  /* Accent line */
  doc.setDrawColor(aR,aG,aB);doc.setLineWidth(0.6);doc.line(M,y+2,W-M,y+2);y+=8;

  /* ── Company details ── */
  if(companyName||companyAddr){
    doc.setFontSize(8);doc.setTextColor(100,100,100);doc.setFont("helvetica","normal");
    const details=[companyName,companyAddr,companyEmail].filter(Boolean).join(" · ");
    doc.text(details,M,y);y+=6;
  }

  /* ── Sections ── */
  sections.forEach(sec=>{
    /* Page break check */
    if(y>H-30){doc.addPage();y=M;}
    /* Section heading */
    if(sec.heading){
      doc.setFontSize(12);doc.setFont("helvetica","bold");doc.setTextColor(40,40,40);
      doc.text(sec.heading,M,y);y+=6;
      doc.setDrawColor(220,220,220);doc.setLineWidth(0.3);doc.line(M,y,W-M,y);y+=4;
    }
    /* KPI row */
    if(sec.kpis){
      const kw=CW/sec.kpis.length;
      sec.kpis.forEach((kpi,i)=>{
        const kx=M+i*kw;
        doc.setFontSize(8);doc.setFont("helvetica","normal");doc.setTextColor(130,130,130);
        doc.text(kpi.label,kx+2,y);
        doc.setFontSize(13);doc.setFont("helvetica","bold");
        const vc=kpi.color==="green"?[34,139,34]:kpi.color==="red"?[200,40,40]:[40,40,40];
        doc.setTextColor(vc[0],vc[1],vc[2]);
        doc.text(kpi.value,kx+2,y+6);
      });
      y+=14;
    }
    /* Table */
    if(sec.table){
      const {headers,rows,totals}=sec.table;
      const colW=CW/headers.length;
      /* Header row */
      doc.setFillColor(245,245,245);doc.rect(M,y-1,CW,6,"F");
      doc.setFontSize(8);doc.setFont("helvetica","bold");doc.setTextColor(80,80,80);
      headers.forEach((h,i)=>{
        const align=i===0?"left":"right";
        const tx=i===0?M+2:M+colW*(i+1)-2;
        doc.text(h,tx,y+3,{align});
      });
      y+=8;
      /* Data rows */
      doc.setFont("helvetica","normal");doc.setTextColor(60,60,60);doc.setFontSize(8);
      rows.forEach(row=>{
        if(y>H-25){doc.addPage();y=M;}
        row.forEach((cell,i)=>{
          const align=i===0?"left":"right";
          const tx=i===0?M+2:M+colW*(i+1)-2;
          doc.text(String(cell),tx,y,{align});
        });
        y+=5;
        doc.setDrawColor(240,240,240);doc.setLineWidth(0.15);doc.line(M,y-1.5,W-M,y-1.5);
      });
      /* Totals row */
      if(totals){
        doc.setFont("helvetica","bold");doc.setFontSize(9);
        doc.setFillColor(aR,aG,aB);doc.rect(M,y-1,CW,7,"F");
        doc.setTextColor(255,255,255);
        totals.forEach((cell,i)=>{
          const align=i===0?"left":"right";
          const tx=i===0?M+2:M+colW*(i+1)-2;
          doc.text(String(cell),tx,y+3,{align});
        });
        y+=10;
      }
      y+=4;
    }
    /* Simple rows (label + value) */
    if(sec.rows){
      sec.rows.forEach(row=>{
        if(y>H-25){doc.addPage();y=M;}
        doc.setFontSize(9);doc.setFont("helvetica","normal");doc.setTextColor(80,80,80);
        doc.text(row.label,M+2,y);
        const vc=row.color==="green"?[34,139,34]:row.color==="red"?[200,40,40]:[40,40,40];
        doc.setTextColor(vc[0],vc[1],vc[2]);doc.setFont("helvetica","bold");
        doc.text(row.value,W-M-2,y,{align:"right"});
        y+=5.5;
        doc.setDrawColor(240,240,240);doc.setLineWidth(0.15);doc.line(M,y-1.5,W-M,y-1.5);
      });
      y+=4;
    }
    /* Horizontal bar chart (category proportions) */
    if(sec.bars){
      const {items,total,barColor}=sec.bars;
      const maxBarW=CW*0.55;
      const barH=4;
      const [bR,bG,bB]=barColor?hexToRgb(barColor):[aR,aG,aB];
      items.forEach(item=>{
        if(y>H-25){doc.addPage();y=M;}
        const pct=total>0?item.value/total:0;
        /* Label */
        doc.setFontSize(8);doc.setFont("helvetica","normal");doc.setTextColor(60,60,60);
        doc.text(String(item.label),M+2,y);
        /* Bar background */
        const barX=M+CW*0.35;
        doc.setFillColor(240,240,240);doc.rect(barX,y-3,maxBarW,barH,"F");
        /* Bar fill */
        if(pct>0){
          const a=Math.max(0.2,pct);
          doc.setFillColor(bR,bG,bB);doc.rect(barX,y-3,maxBarW*pct,barH,"F");
        }
        /* Value + pct */
        doc.setFontSize(8);doc.setFont("helvetica","bold");doc.setTextColor(60,60,60);
        doc.text(`${item.formatted}  (${(pct*100).toFixed(0)}%)`,barX+maxBarW+3,y,{align:"left"});
        y+=7;
      });
      y+=4;
    }
    /* Summary bar (colored footer row) */
    if(sec.summary){
      doc.setFillColor(aR,aG,aB);doc.rect(M,y-1,CW,8,"F");
      doc.setTextColor(255,255,255);doc.setFontSize(10);doc.setFont("helvetica","bold");
      doc.text(sec.summary.label,M+4,y+4);
      doc.text(sec.summary.value,W-M-4,y+4,{align:"right"});
      y+=12;
    }
  });

  /* ── Footer ── */
  const pageCount=doc.getNumberOfPages();
  for(let p=1;p<=pageCount;p++){
    doc.setPage(p);
    doc.setDrawColor(aR,aG,aB);doc.setLineWidth(0.4);doc.line(M,H-14,W-M,H-14);
    doc.setFontSize(7);doc.setTextColor(140,140,140);doc.setFont("helvetica","normal");
    doc.text(`${companyName} — Generated ${new Date().toLocaleDateString("en-GB")}`,M,H-10);
    doc.text(`Page ${p} of ${pageCount}`,W-M,H-10,{align:"right"});
  }

  doc.save(`${title.replace(/\s+/g,"_")}_${new Date().toISOString().slice(0,10)}.pdf`);
  }catch(err){
    console.error("PDF generation failed:",err);
    try{
      const el=document.createElement("div");
      el.textContent=lang==="ar"?"فشل إنشاء ملف PDF. يرجى المحاولة مرة أخرى.":"PDF generation failed. Please try again.";
      Object.assign(el.style,{position:"fixed",top:"16px",left:"50%",transform:"translateX(-50%)",zIndex:"99999",padding:"10px 20px",background:"#FF3B30",color:"white",borderRadius:"14px",fontSize:"13px",fontWeight:"600",boxShadow:"0 6px 24px rgba(0,0,0,.2)",textAlign:"center"});
      document.body.appendChild(el);
      setTimeout(()=>el.remove(),4000);
    }catch(e){}
  }
}

/* ══ FINANCIAL HELPERS ═════════════════════════════════════ */
function getDateRange(period, customStart, customEnd) {
  const now = new Date();
  if (period === "month") {
    return {
      start: new Date(now.getFullYear(), now.getMonth(), 1),
      end:   new Date(now.getFullYear(), now.getMonth()+1, 0),
      label: now.toLocaleDateString("en-GB", {month:"long", year:"numeric"}),
    };
  }
  if (period === "year") {
    return {
      start: new Date(now.getFullYear(), 0, 1),
      end:   new Date(now.getFullYear(), 11, 31),
      label: String(now.getFullYear()),
    };
  }
  if (period === "custom" && customStart && customEnd) {
    return {
      start: new Date(customStart),
      end:   new Date(customEnd),
      label: `${customStart} → ${customEnd}`,
    };
  }
  return { start: new Date(0), end: new Date(), label: "All time" };
}

function inRange(dateStr, range) {
  if (!dateStr) return false;
  /* Parse YYYY-MM-DD as local date (not UTC) to match range boundaries.
     Normalize to date-only (strip time) so a dateStr with a time component
     still compares correctly against midnight-based range boundaries. */
  const parts = dateStr.split("-");
  const d = parts.length === 3
    ? new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2]))
    : new Date(dateStr);
  const dDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const sDate = new Date(range.start.getFullYear(), range.start.getMonth(), range.start.getDate());
  const eDate = new Date(range.end.getFullYear(), range.end.getMonth(), range.end.getDate());
  return dDate >= sDate && dDate <= eDate;
}

/* ══ Shared Report chart components ═══════════════════════ */
function reportChartColors(anchorColor){
  const a=anchorColor||"#C9A84C";
  return[a,`color-mix(in srgb, ${a} 75%, #000)`,`color-mix(in srgb, ${a} 55%, #888)`,`color-mix(in srgb, ${a} 40%, #aaa)`,`color-mix(in srgb, ${a} 25%, #ccc)`,`color-mix(in srgb, ${a} 15%, #ddd)`];
}

function ReportDonutChart({data,colors,total,label,totalColor}){
  const size=180,cx=size/2,cy=size/2,r=65,strokeW=28;
  const circ=2*Math.PI*r;
  let offset=0;
  return(
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{display:"block",margin:"0 auto"}}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--g100)" strokeWidth={strokeW}/>
      {data.map(([name,val],i)=>{
        const pct=total?val/total:0;
        const dash=circ*pct;
        const gap=circ-dash;
        const seg=<circle key={name} cx={cx} cy={cy} r={r} fill="none" stroke={colors[i%colors.length]} strokeWidth={strokeW}
          strokeDasharray={`${dash} ${gap}`} strokeDashoffset={-offset} strokeLinecap="butt"
          transform={`rotate(-90 ${cx} ${cy})`} style={{transition:"stroke-dasharray .5s,stroke-dashoffset .5s"}}/>;
        offset+=dash;
        return seg;
      })}
      <text x={cx} y={cy-8} textAnchor="middle" style={{fontSize:16,fontWeight:700,fontFamily:"var(--fm)",fill:totalColor}}>{fmtCompact(total)}</text>
      <text x={cx} y={cy+10} textAnchor="middle" style={{fontSize:10,fill:"var(--g400)",fontWeight:500}}>{label}</text>
    </svg>
  );
}

function ReportLegend({data,colors,total}){
  return(
    <div style={{marginTop:12}}>
      {data.map(([name,val],i)=>{
        const pct=total?(val/total*100):0;
        return(
          <div key={name} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderBottom:"1px solid var(--g50)"}}>
            <div style={{width:12,height:12,borderRadius:3,background:colors[i%colors.length],flexShrink:0}}/>
            <div style={{flex:1,fontSize:13,color:"var(--ink)"}}>{name}</div>
            <div style={{fontSize:12,fontWeight:600,color:"var(--g500)",minWidth:40,textAlign:"right"}}>{pct.toFixed(1)}%</div>
            <div style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,color:"var(--ink)",minWidth:80,textAlign:"right"}}>{fmtCompact(val)}</div>
          </div>
        );
      })}
    </div>
  );
}

/* ══ Shared Report period picker ══════════════════════════ */
const REPORT_MONTHS=["January","February","March","April","May","June","July","August","September","October","November","December"];
const REPORT_YEARS=Array.from({length:10},(_,i)=>new Date().getFullYear()-4+i);

function useReportPeriod(initialLocFilter){
  const now=new Date();
  const [periodMode,setPeriodMode]=useState("month");
  const [filterLoc,setFilterLoc]=useState(initialLocFilter||"");
  const [pickMonth,setPickMonth]=useState(now.getMonth());
  const [pickYear,setPickYear]=useState(now.getFullYear());
  const [customStart,setCustomStart]=useState("");
  const [customEnd,setCustomEnd]=useState("");

  const range=(()=>{
    if(periodMode==="month")return getDateRange("month","","");
    if(periodMode==="year")return getDateRange("year","","");
    if(periodMode==="pick"){
      const start=new Date(pickYear,pickMonth,1);
      const end=new Date(pickYear,pickMonth+1,0);
      const label=start.toLocaleDateString("en-GB",{month:"long",year:"numeric"});
      return{start,end,label};
    }
    if(periodMode==="custom"&&customStart&&customEnd)return getDateRange("custom",customStart,customEnd);
    return getDateRange("month","","");
  })();

  return{periodMode,setPeriodMode,filterLoc,setFilterLoc,pickMonth,setPickMonth,pickYear,setPickYear,customStart,setCustomStart,customEnd,setCustomEnd,range};
}

function ReportPeriodPicker({period,locations,lang,t}){
  const{periodMode,setPeriodMode,filterLoc,setFilterLoc,pickMonth,setPickMonth,pickYear,setPickYear,customStart,setCustomStart,customEnd,setCustomEnd}=period;
  return(
    <div className="rep-period-card">
      <div style={{display:"flex",gap:6,width:"100%"}}>
        {[
          {k:"month",  l:lang==="ar"?"هذا الشهر":"This Month"},
          {k:"year",   l:lang==="ar"?"هذا العام":"This Year"},
          {k:"pick",   l:lang==="ar"?"اختر شهر":"Pick Month"},
          {k:"custom", l:lang==="ar"?"مخصص":"Custom"},
        ].map(m=>(
          <button key={m.k} className={`btn btn-sm ${periodMode===m.k?"btn-p":"btn-s"}`} style={{flex:1,justifyContent:"center",textAlign:"center"}} onClick={()=>setPeriodMode(m.k)}>{m.l}</button>
        ))}
      </div>
      {periodMode==="pick"&&(
        <div style={{display:"flex",gap:8,alignItems:"center",marginTop:12,width:"100%"}}>
          <select className="fs" value={pickMonth} onChange={e=>setPickMonth(parseInt(e.target.value))} style={{flex:1,fontSize:13,padding:"8px 12px"}}>
            {REPORT_MONTHS.map((m,i)=><option key={i} value={i}>{m}</option>)}
          </select>
          <select className="fs" value={pickYear} onChange={e=>setPickYear(parseInt(e.target.value))} style={{flex:1,fontSize:13,padding:"8px 12px"}}>
            {REPORT_YEARS.map(y=><option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      )}
      {periodMode==="custom"&&(
        <div style={{display:"flex",gap:8,alignItems:"center",marginTop:12,width:"100%"}}>
          <input className="fi" type="date" value={customStart} onChange={e=>setCustomStart(e.target.value)} style={{flex:1,fontSize:13,padding:"8px 12px"}}/>
          <span style={{color:"var(--g400)",fontSize:13,flexShrink:0}}>→</span>
          <input className="fi" type="date" value={customEnd} onChange={e=>setCustomEnd(e.target.value)} style={{flex:1,fontSize:13,padding:"8px 12px"}}/>
        </div>
      )}
      <select className="fs" value={filterLoc} onChange={e=>setFilterLoc(e.target.value)} style={{width:"100%",marginTop:12,fontSize:13,padding:"8px 12px"}}>
        <option value="">{t.global||(lang==="ar"?"جميع المواقع":"All Locations")}</option>
        {locations.map(l=><option key={l.id} value={l.id}>{localName(l, lang)}</option>)}
      </select>
    </div>
  );
}

/* ══ Shared forex conversion hook ═════════════════════════ */
function useFxRates(holdings,liabilities,wsCur){
  const [fxRates,setFxRates]=useState({});
  const [fxLoading,setFxLoading]=useState(false);
  useEffect(()=>{
    const foreignCurs=[...new Set([...(holdings||[]).map(h=>h.currency),...liabilities.map(l=>l.currency)].filter(c=>c&&c!==wsCur))];
    if(!foreignCurs.length){setFxLoading(false);return;}
    setFxLoading(true);
    Promise.all(foreignCurs.map(c=>fetchForexRate(c,wsCur).then(rate=>({c,rate})))).then(results=>{
      const rates={};
      results.forEach(({c,rate})=>{if(rate)rates[c]=rate;});
      setFxRates(rates);
    }).finally(()=>setFxLoading(false));
  },[(holdings||[]).length,liabilities.length,wsCur]);
  const toWsCur=(amount,cur)=>{
    if(!cur||cur===wsCur)return amount||0;
    return(amount||0)*(fxRates[cur]||1);
  };
  return{fxRates,fxLoading,toWsCur};
}

/* ══ PROFIT & LOSS MODULE ══════════════════════════════════ */
/* ══ REPORTS MODULE — Unified wrapper ═════════════════════
   Tabs: P&L · Balance Sheet · Cash Flow
══════════════════════════════════════════════════════════ */
function ReportTabSwitcher({activeTab,setActiveTab}){
  const tabs=[{k:"pnl",l:"Profit & Loss"},{k:"balance",l:"Balance Sheet"},{k:"cashflow",l:"Cash Flow"}];
  return(<>
    <div className="rep-tabs-desktop" style={{display:"flex",gap:0,marginBottom:16}}>
      {tabs.map(tb=>(
        <div key={tb.k} className={`tab ${activeTab===tb.k?"active":""}`} onClick={()=>setActiveTab(tb.k)} style={{marginBottom:0}}>{tb.l}</div>
      ))}
    </div>
    <div className="rep-tabs-mobile" style={{display:"none",marginBottom:14}}>
      <div className="exp-status-tabs" style={{display:"flex"}}>
        {tabs.map(tb=>(
          <button key={tb.k} className={`exp-status-tab ${activeTab===tb.k?"active":""}`} onClick={()=>setActiveTab(tb.k)} style={{fontSize:11,padding:"8px 6px"}}>{tb.l}</button>
        ))}
      </div>
    </div>
  </>);
}
function ReportsModule({state, dispatch, t}) {
  const [tab, setTab] = useState("pnl");
  return (
    <div>
      {tab==="pnl"          && <PnLModule          state={state} dispatch={dispatch} t={t} activeTab={tab} setActiveTab={setTab}/>}
      {tab==="balance"      && <BalanceSheetModule  state={state} dispatch={dispatch} t={t} activeTab={tab} setActiveTab={setTab}/>}
      {tab==="cashflow"     && <CashFlowModule      state={state} dispatch={dispatch} t={t} activeTab={tab} setActiveTab={setTab}/>}
    </div>
  );
}

/* Inline number editor used for margin target, overall expense budget,
   and per-category budgets inside PnLModule. Displays the value (or a
   placeholder when null) and, when clicked, swaps in a number input with
   save/cancel. Empty input saves as null (= unset). */
function InlineNumEdit({value, onSave, suffix, placeholder, ariaLabel, formatDisplay, min=0}){
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const startEdit = () => { setDraft(value==null?"":String(value)); setEditing(true); };
  const cancel = () => { setEditing(false); setDraft(""); };
  const save = () => {
    const trimmed = draft.trim();
    if (trimmed === "") { onSave(null); setEditing(false); return; }
    const n = parseFloat(trimmed);
    if (!isFinite(n) || n < min) return; /* keep editor open on invalid input */
    onSave(n);
    setEditing(false);
  };
  const onKey = (e) => { if(e.key==="Enter") save(); if(e.key==="Escape") cancel(); };
  if (editing) {
    return (
      <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
        <input autoFocus type="number" min={min} step="any" value={draft}
          onChange={(e)=>setDraft(e.target.value)} onKeyDown={onKey}
          placeholder={placeholder} aria-label={ariaLabel}
          style={{width:90,fontSize:12,padding:"3px 6px",border:"1px solid var(--g200)",borderRadius:4,fontFamily:"var(--fm)"}}/>
        {suffix && <span style={{fontSize:11,color:"var(--g500)"}}>{suffix}</span>}
        <button type="button" onClick={save}   aria-label="Save"
          style={{padding:"2px 6px",fontSize:12,border:"none",background:"var(--grn)",color:"white",borderRadius:3,cursor:"pointer",lineHeight:1}}>✓</button>
        <button type="button" onClick={cancel} aria-label="Cancel"
          style={{padding:"2px 6px",fontSize:12,border:"none",background:"var(--g200)",color:"var(--ink)",borderRadius:3,cursor:"pointer",lineHeight:1}}>✕</button>
      </span>
    );
  }
  return (
    <button type="button" onClick={startEdit} aria-label={ariaLabel}
      style={{background:"none",border:"none",padding:0,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:4,color:"inherit",fontFamily:"inherit",fontSize:"inherit"}}>
      {value==null
        ? <span style={{fontSize:11,color:"var(--g400)",fontStyle:"italic"}}>{placeholder||"Set"}</span>
        : <span>{formatDisplay?formatDisplay(value):value}{suffix?` ${suffix}`:""}</span>}
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{opacity:.55}}><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
    </button>
  );
}

function PnLModule({state,dispatch,t,activeTab,setActiveTab}) {
  const {income, expenses, expenseCats, incomeCats, users, vehicles, locations, holdings, locFilter, lang} = state;
  const incTopCats=(incomeCats||[]).filter(c=>!c.parent);
  const incSubCats=(catId)=>(incomeCats||[]).filter(c=>c.parent===catId);
  const [incF,setIncF]=useState({amount:"",catId:incTopCats[0]?.id||"",subId:"",source:"",status:"Realised",date:TODAY,dueDate:"",notes:""});
  const sif=(k,v)=>setIncF(p=>({...p,[k]:v}));
  const resolveIncFin=(catId,subId)=>{const c=(incomeCats||[]).find(x=>x.id===(subId||catId));return{finType:c?.finType||"Income",subType:c?.subType||"Operating"};};
  const defaultUser=users?.[0]?.id||"u1";
  const period=useReportPeriod(locFilter);
  const {range,filterLoc}=period;
  const [compare,setCompare]=useState(false);

  /* Owner gate: only workspace owners see edit controls for targets/budgets. */
  const currentUser = state.currentUser || {};
  const canEditTargets = currentUser?.role === "owner";

  /* Workspace-wide margin target & overall monthly expense budget (both nullable). */
  const financialTargets = state.financialTargets || {};
  const marginTargetPct = financialTargets.marginTargetPct;
  const expenseBudgetMonthly = financialTargets.expenseBudgetMonthly;

  /* Period length in whole months (≥1). Monthly budgets scale by this
     factor so the figures shown in P&L match the selected range. */
  const periodMonths = useMemo(() => {
    const ms = range.end.getTime() - range.start.getTime();
    return Math.max(1, Math.round(ms / (30.44 * 86400000)));
  }, [range]);
  const scaledExpenseBudget = expenseBudgetMonthly != null ? expenseBudgetMonthly * periodMonths : null;

  const saveMarginTarget = (v) => dispatch({type:"SET_FINANCIAL_TARGETS", patch:{marginTargetPct:v}});
  const saveExpenseBudget = (v) => dispatch({type:"SET_FINANCIAL_TARGETS", patch:{expenseBudgetMonthly:v}});
  const saveCatBudget = (id, v) => dispatch({type:"SET_CAT_BUDGET", id, monthlyBudget:v});

  /* Build the section list used by both desktop & mobile PDF buttons.
     Declared as a function (not useMemo) so it reads the latest totals
     at click time. Budget columns and the Budget vs Actual summary are
     only included when there is budget/target data to show. */
  const buildPdfSections = () => {
    const fS = fmtSAR;
    const marginPct = totalIncome>0 ? (netProfit/totalIncome)*100 : null;
    const marginCell = marginPct==null
      ? "—"
      : marginTargetPct!=null
        ? `${marginPct.toFixed(1)}% / target ${marginTargetPct}%`
        : `${marginPct.toFixed(1)}%`;
    const hasCatBudgets = catDetails.some(d=>d.monthlyBudget!=null);
    const scaledLabel = periodMonths>1 ? `Budget (${periodMonths} mo)` : "Budget (mo)";
    const expHeaders = hasCatBudgets ? ["Category","Amount",scaledLabel,"Var %"] : ["Category","Amount"];
    const expRows = catDetails.length
      ? catDetails.map(row=>{
          const scaled = row.monthlyBudget!=null ? row.monthlyBudget*periodMonths : null;
          const variance = scaled!=null && scaled>0 ? (row.actual/scaled)*100 : null;
          return hasCatBudgets
            ? [row.name, fS(row.actual), scaled==null?"—":fS(scaled), variance==null?"—":`${variance.toFixed(0)}%`]
            : [row.name, fS(row.actual)];
        })
      : catRows.map(([n,v])=>hasCatBudgets?[n,fS(v),"—","—"]:[n,fS(v)]);
    const expTotals = hasCatBudgets
      ? ["Total Expenses", fS(totalExpenses), scaledExpenseBudget==null?"—":fS(scaledExpenseBudget),
         scaledExpenseBudget==null||scaledExpenseBudget===0?"—":`${((totalExpenses/scaledExpenseBudget)*100).toFixed(0)}%`]
      : ["Total Expenses", fS(totalExpenses)];

    const budgetSection = (marginTargetPct!=null || scaledExpenseBudget!=null)
      ? [{heading:"Budget vs Actual", rows:[
          ...(marginTargetPct!=null ? [{label:"Margin", value:marginCell,
              color: marginPct==null ? undefined : (marginPct>=marginTargetPct?"green":"red")}] : []),
          ...(scaledExpenseBudget!=null ? [{label:`Total Expenses (${periodMonths} mo)`,
              value:`${fS(totalExpenses)} / ${fS(scaledExpenseBudget)} (${scaledExpenseBudget>0?((totalExpenses/scaledExpenseBudget)*100).toFixed(0):"—"}%)`,
              color: totalExpenses>scaledExpenseBudget ? "red" : undefined}] : []),
        ]}]
      : [];

    return [
      {kpis:[
        {label:"Total Income",value:fS(totalIncome),color:"green"},
        {label:"Total Expenses",value:fS(totalExpenses),color:"red"},
        {label:netProfit>=0?"Net Profit":"Net Loss",value:fS(Math.abs(netProfit)),color:netProfit>=0?"green":"red"},
        {label:"Margin",value:marginCell}
      ]},
      ...budgetSection,
      {heading:"Income by Category", table:{headers:["Category","Amount"], rows:incCatRows.map(([n,v])=>[n,fS(v)]), totals:["Total Income",fS(totalIncome)]}},
      ...(incCatRows.length>0?[{bars:{items:incCatRows.map(([n,v])=>({label:n,value:v,formatted:fS(v)})),total:totalIncome,barColor:"#228B22"}}]:[]),
      {heading:"Expenses by Category", table:{headers:expHeaders, rows:expRows, totals:expTotals}},
      ...(catRows.length>0?[{bars:{items:catRows.map(([n,v])=>({label:n,value:v,formatted:fS(v)})),total:totalExpenses,barColor:"#C82828"}}]:[]),
      {heading:"Income by Sub-type", table:{headers:["Sub-type","Amount"], rows:Object.entries(byIncSubType).map(([st,v])=>[st,fS(v)]), totals:["Total",fS(totalIncome)]}},
      {heading:"Expenses by Sub-type", table:{headers:["Sub-type","Amount"], rows:Object.entries(bySubType).map(([st,v])=>[st,fS(v)]), totals:["Total",fS(totalExpenses)]}},
      {summary:{label:netProfit>=0?"Net Profit":"Net Loss",value:fS(Math.abs(netProfit))}}
    ];
  };
  const runPdfExport = () => generateReportPDF({
    title:"Profit & Loss",
    subtitle:`${range.label}${filterLoc?" · "+locName(locations,filterLoc):""}`,
    state, lang, sections: buildPdfSections(),
  });

  /* Previous period range (same duration, shifted back) */
  const prevRange=useMemo(()=>{
    const dur=range.end-range.start;
    const pEnd=new Date(range.start.getTime()-1);
    const pStart=new Date(pEnd.getTime()-dur);
    const lbl=pStart.toLocaleDateString("en-GB",{month:"short",year:"numeric"})+" → "+pEnd.toLocaleDateString("en-GB",{month:"short",year:"numeric"});
    return{start:pStart,end:pEnd,label:lbl};
  },[range]);

  const {incLines,expLines,totalIncome,totalExpenses,netProfit,bySubType,catRows,catDetails,incCatRows,byIncSubType}=useMemo(()=>{
    const _inc=income.filter(i=>{
      const inPeriod=inRange(i.date,range);
      const locMatch=!filterLoc||!i.locId||i.locId===filterLoc;
      const isIncome=!i.finType||i.finType==="Income";
      return inPeriod&&locMatch&&isIncome&&i.status==="Realised";
    });
    const _exp=expenses.filter(e=>{
      const inPeriod=inRange(e.date,range);
      const locMatch=!filterLoc||!e.locId||e.locId===filterLoc;
      const isExpense=!e.finType||e.finType==="Expense";
      return inPeriod&&locMatch&&isExpense;
    });
    const _totalInc=_inc.reduce((a,i)=>a+i.amount,0);
    const _totalExp=_exp.reduce((a,e)=>a+e.amount,0);
    // Group expenses by subType
    const _bySubType={};
    _exp.forEach(e=>{const st=e.subType||"Operating";_bySubType[st]=(_bySubType[st]||0)+e.amount;});
    // Expense breakdown by PARENT category
    const _byCat={};
    _exp.forEach(e=>{
      const cat=expenseCats.find(c=>c.id===e.catId);
      const parentCat=cat?.parent?expenseCats.find(c=>c.id===cat.parent):cat;
      const resolved=parentCat||cat;
      const name=resolved?(localName(resolved, lang)):"Uncategorised";
      _byCat[name]=(_byCat[name]||0)+e.amount;
    });
    // Income breakdown by PARENT category
    const _byIncCat={};
    _inc.forEach(i=>{
      const cat=(incomeCats||[]).find(c=>c.id===i.catId);
      const parentCat=cat?.parent?(incomeCats||[]).find(c=>c.id===cat.parent):cat;
      const resolved=parentCat||cat;
      const name=resolved?(localName(resolved, lang)):"Uncategorised";
      _byIncCat[name]=(_byIncCat[name]||0)+i.amount;
    });
    // Income by subType
    const _byIncSubType={};
    _inc.forEach(i=>{const st=i.subType||"Operating";_byIncSubType[st]=(_byIncSubType[st]||0)+i.amount;});
    // Parent-category details with budget metadata — used for the budget
    // columns in the Expenses by Category card. Keyed by id so edit
    // actions target the right row. Includes rows with zero spend but a
    // budget set (so owners can see/clear them).
    const _catDetails=[];
    (expenseCats||[]).forEach(c=>{
      if(c.parent!=null) return; /* only parent cats get budgets */
      const name=localName(c, lang);
      const actual=_byCat[name]||0;
      const hasBudget=c.monthlyBudget!=null;
      if(actual<=0 && !hasBudget) return;
      _catDetails.push({id:c.id, name, actual, monthlyBudget:c.monthlyBudget});
    });
    if(_byCat["Uncategorised"]){
      _catDetails.push({id:null, name:"Uncategorised", actual:_byCat["Uncategorised"], monthlyBudget:null});
    }
    _catDetails.sort((a,b)=>b.actual-a.actual);
    return{incLines:_inc,expLines:_exp,totalIncome:_totalInc,totalExpenses:_totalExp,netProfit:_totalInc-_totalExp,bySubType:_bySubType,catRows:Object.entries(_byCat).sort((a,b)=>b[1]-a[1]),catDetails:_catDetails,incCatRows:Object.entries(_byIncCat).sort((a,b)=>b[1]-a[1]),byIncSubType:_byIncSubType};
  },[income,expenses,expenseCats,incomeCats,range,filterLoc,lang]);

  /* Previous period aggregation (only computed when compare is on) */
  const prev=useMemo(()=>{
    if(!compare)return null;
    const pInc=income.filter(i=>inRange(i.date,prevRange)&&(!filterLoc||!i.locId||i.locId===filterLoc)&&(!i.finType||i.finType==="Income")&&i.status==="Realised");
    const pExp=expenses.filter(e=>inRange(e.date,prevRange)&&(!filterLoc||!e.locId||e.locId===filterLoc)&&(!e.finType||e.finType==="Expense"));
    const pTotalInc=pInc.reduce((a,i)=>a+i.amount,0);
    const pTotalExp=pExp.reduce((a,e)=>a+e.amount,0);
    const pByCat={};
    pExp.forEach(e=>{
      const cat=expenseCats.find(c=>c.id===e.catId);
      const parentCat=cat?.parent?expenseCats.find(c=>c.id===cat.parent):cat;
      const resolved=parentCat||cat;
      const name=resolved?(localName(resolved, lang)):"Uncategorised";
      pByCat[name]=(pByCat[name]||0)+e.amount;
    });
    const pByIncCat={};
    pInc.forEach(i=>{
      const cat=(incomeCats||[]).find(c=>c.id===i.catId);
      const parentCat=cat?.parent?(incomeCats||[]).find(c=>c.id===cat.parent):cat;
      const resolved=parentCat||cat;
      const name=resolved?(localName(resolved, lang)):"Uncategorised";
      pByIncCat[name]=(pByIncCat[name]||0)+i.amount;
    });
    return{totalIncome:pTotalInc,totalExpenses:pTotalExp,netProfit:pTotalInc-pTotalExp,byCat:pByCat,byIncCat:pByIncCat,label:prevRange.label};
  },[compare,income,expenses,expenseCats,incomeCats,prevRange,filterLoc,lang]);

  /* Delta formatting helper */
  const fmtDelta=(cur,prv)=>{
    if(prv===undefined||prv===null)return null;
    const diff=cur-prv;
    const pct=prv!==0?((diff/prv)*100).toFixed(0):diff>0?"+∞":"—";
    return{diff,pct:prv!==0?(diff>=0?"+":"")+pct+"%":pct,color:diff>0?"var(--grn)":diff<0?"var(--red)":"var(--g400)"};
  };

  return (
    <div className="page rep-page-bg">
      {/* Desktop header */}
      <div className="ph rep-hero-desktop" style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div>
          <div className="ptitle">{lang==="ar"?"الأرباح والخسائر":"Profit & Loss"}</div>
          <div className="psub">{range.label}{filterLoc?" · "+locName(locations,filterLoc):""}</div>
        </div>
        <button className="btn btn-s btn-sm" style={{gap:6,fontSize:12}} onClick={runPdfExport}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 18 15 15"/></svg>
          PDF
        </button>
      </div>

      {/* Mobile hero header */}
      <div className="rep-hero">
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div className="rep-hero-greeting">{lang==="ar"?"الأرباح والخسائر":"Profit & Loss"}</div>
          <button className="btn btn-s btn-sm" style={{gap:4,fontSize:11,padding:"6px 10px"}} onClick={runPdfExport}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 18 15 15"/></svg>
            PDF
          </button>
        </div>
        <div className="rep-hero-sub">{range.label}{filterLoc?" · "+locName(locations,filterLoc):""}</div>
        <ReportTabSwitcher activeTab={activeTab} setActiveTab={setActiveTab}/>
        <div className="rep-summary-glass">
          <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:10}}>
            <div style={{fontFamily:"var(--fm)",fontSize:22,fontWeight:600,color:netProfit>=0?"var(--grn)":"var(--red)",letterSpacing:"-.02em",lineHeight:1}}>{netProfit>=0?"+":"-"}{fmtCompact(Math.abs(netProfit))}</div>
            <div style={{fontSize:11,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".06em"}}>{netProfit>=0?(lang==="ar"?"صافي الربح":"net profit"):(lang==="ar"?"صافي الخسارة":"net loss")}</div>
          </div>
          {(totalIncome+totalExpenses)>0&&<div style={{display:"flex",height:6,borderRadius:3,overflow:"hidden",marginBottom:12,gap:2}}>
            <div style={{width:`${totalIncome/(totalIncome+totalExpenses)*100}%`,background:"var(--grn)",borderRadius:3,minWidth:3}}/>
            <div style={{width:`${totalExpenses/(totalIncome+totalExpenses)*100}%`,background:"var(--red)",borderRadius:3,minWidth:3}}/>
          </div>}
          <div style={{display:"flex",justifyContent:"space-between",gap:8}}>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:"var(--grn)"}}/>
              <div><div style={{fontSize:10,color:"var(--g400)",fontWeight:500,lineHeight:1}}>{lang==="ar"?"الدخل":"Income"}</div><div style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,color:"var(--grn)",lineHeight:1.3}}>{fmtCompact(totalIncome)}</div></div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:"var(--red)"}}/>
              <div><div style={{fontSize:10,color:"var(--g400)",fontWeight:500,lineHeight:1}}>{lang==="ar"?"المصاريف":"Expenses"}</div><div style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,color:"var(--red)",lineHeight:1.3}}>{fmtCompact(totalExpenses)}</div></div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:"var(--g300)"}}/>
              <div>
                <div style={{fontSize:10,color:"var(--g400)",fontWeight:500,lineHeight:1}}>{lang==="ar"?"الهامش":"Margin"}</div>
                <div style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,lineHeight:1.3}}>
                  {totalIncome>0?`${((netProfit/totalIncome)*100).toFixed(1)}%`:"—"}
                  {marginTargetPct!=null && totalIncome>0 && (
                    <span style={{fontSize:10,fontWeight:500,color:"var(--g400)",marginInlineStart:4}}>
                      {lang==="ar"?"/ هدف":"/ target"} {marginTargetPct}%
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop: tabs + period picker row */}
      <div className="rep-hero-desktop" style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
        <div><ReportTabSwitcher activeTab={activeTab} setActiveTab={setActiveTab}/></div>
      </div>

      {/* Period picker */}
      <ReportPeriodPicker period={period} locations={locations} lang={lang} t={t}/>

      {/* Empty state guidance */}
      {totalIncome===0&&totalExpenses===0&&(
        <div className="rep-cat-card" style={{padding:24,textAlign:"center",marginBottom:20}}>
          <div style={{fontSize:28,marginBottom:8,opacity:0.3}}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{display:"inline"}}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
          </div>
          <div style={{fontSize:15,fontWeight:600,color:"var(--ink)",marginBottom:6,fontFamily:"var(--fi)"}}>{lang==="ar"?"لا توجد بيانات مالية بعد":"No financial data yet"}</div>
          <div style={{fontSize:13,color:"var(--g400)",lineHeight:1.5,maxWidth:360,margin:"0 auto"}}>{lang==="ar"?"أضف دخلك ومصاريفك من وحدتي الدخل والمصاريف لتظهر تقارير الأرباح والخسائر هنا.":"Add income and expenses from the Income and Expenses modules to see your Profit & Loss report here."}</div>
        </div>
      )}

      {/* Compare toggle */}
      {(totalIncome>0||totalExpenses>0)&&<div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
        <button className={`btn btn-sm ${compare?"btn-p":"btn-s"}`} style={{gap:6,fontSize:12}} onClick={()=>setCompare(p=>!p)}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
          {lang==="ar"?"مقارنة الفترات":"Compare Periods"}
        </button>
        {compare&&prev&&<span style={{fontSize:11,color:"var(--g400)"}}>{lang==="ar"?"مقابل":"vs"} {prev.label}</span>}
      </div>}

      {/* Period comparison KPIs */}
      {compare&&prev&&(()=>{
        const dInc=fmtDelta(totalIncome,prev.totalIncome);
        const dExp=fmtDelta(totalExpenses,prev.totalExpenses);
        const dNet=fmtDelta(netProfit,prev.netProfit);
        const DeltaBadge=({d})=>d?<span style={{fontSize:10,fontWeight:600,color:d.color,marginInlineStart:4}}>{d.pct}</span>:null;
        return(
          <div className="rep-cat-card" style={{marginBottom:16,padding:16}}>
            <div className="rep-cat-title" style={{marginBottom:10}}>{lang==="ar"?"مقارنة مع الفترة السابقة":"Period-over-Period Comparison"}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8,fontSize:12}}>
              <div style={{fontWeight:600,color:"var(--g400)"}}></div>
              <div style={{fontWeight:600,color:"var(--g400)",textAlign:"right"}}>{lang==="ar"?"الحالي":"Current"}</div>
              <div style={{fontWeight:600,color:"var(--g400)",textAlign:"right"}}>{lang==="ar"?"السابق":"Previous"}</div>
              <div style={{fontWeight:600,color:"var(--g400)",textAlign:"right"}}>{lang==="ar"?"التغير":"Change"}</div>
              <div style={{fontWeight:500,color:"var(--ink)"}}>{lang==="ar"?"الدخل":"Income"}</div>
              <div style={{textAlign:"right",fontFamily:"var(--fm)",fontWeight:600,color:"var(--grn)"}}>{fmtCompact(totalIncome)}</div>
              <div style={{textAlign:"right",fontFamily:"var(--fm)",color:"var(--g500)"}}>{fmtCompact(prev.totalIncome)}</div>
              <div style={{textAlign:"right",fontFamily:"var(--fm)",fontWeight:600,color:dInc?.color}}>{dInc?.pct||"—"}</div>
              <div style={{fontWeight:500,color:"var(--ink)"}}>{lang==="ar"?"المصاريف":"Expenses"}</div>
              <div style={{textAlign:"right",fontFamily:"var(--fm)",fontWeight:600,color:"var(--red)"}}>{fmtCompact(totalExpenses)}</div>
              <div style={{textAlign:"right",fontFamily:"var(--fm)",color:"var(--g500)"}}>{fmtCompact(prev.totalExpenses)}</div>
              <div style={{textAlign:"right",fontFamily:"var(--fm)",fontWeight:600,color:dExp?.color}}>{dExp?.pct||"—"}</div>
              <div style={{fontWeight:500,color:"var(--ink)"}}>{lang==="ar"?"الصافي":"Net"}</div>
              <div style={{textAlign:"right",fontFamily:"var(--fm)",fontWeight:600,color:netProfit>=0?"var(--grn)":"var(--red)"}}>{fmtCompact(netProfit)}</div>
              <div style={{textAlign:"right",fontFamily:"var(--fm)",color:"var(--g500)"}}>{fmtCompact(prev.netProfit)}</div>
              <div style={{textAlign:"right",fontFamily:"var(--fm)",fontWeight:600,color:dNet?.color}}>{dNet?.pct||"—"}</div>
            </div>
          </div>
        );
      })()}

      {/* Budget vs Actual — owner-editable margin target & overall expense budget. */}
      {(() => {
        const anyTarget = marginTargetPct!=null || expenseBudgetMonthly!=null;
        if (!anyTarget && !canEditTargets) return null; /* nothing to show, not editable */
        if (totalIncome===0 && totalExpenses===0 && !canEditTargets) return null;

        const marginPct = totalIncome>0 ? (netProfit/totalIncome)*100 : null;
        const marginStatus = (marginPct==null || marginTargetPct==null)
          ? null
          : (marginPct >= marginTargetPct
              ? "ok"
              : (marginTargetPct - marginPct <= 5 ? "near" : "below"));
        const marginColor = marginStatus==="ok" ? "var(--grn)"
          : marginStatus==="near" ? "var(--amber,#c98f0d)"
          : marginStatus==="below" ? "var(--red)"
          : "var(--ink)";

        const expPct = scaledExpenseBudget>0 ? (totalExpenses/scaledExpenseBudget)*100 : null;
        const overBudget = scaledExpenseBudget!=null && totalExpenses > scaledExpenseBudget;
        const barFillPct = expPct==null ? 0 : Math.min(100, expPct);
        const barColor = overBudget ? "var(--red)" : expPct!=null && expPct>=85 ? "var(--amber,#c98f0d)" : "var(--grn)";

        const scaledLabel = periodMonths>1
          ? (lang==="ar"?`الميزانية (${periodMonths} أشهر)`:`Budget (${periodMonths} months)`)
          : (lang==="ar"?"الميزانية الشهرية":"Monthly budget");

        return (
          <div className="rep-cat-card" style={{marginBottom:16,padding:16}}>
            <div className="rep-cat-title" style={{marginBottom:12}}>{lang==="ar"?"الميزانية مقابل الفعلي":"Budget vs Actual"}</div>

            {/* Margin target row */}
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,paddingBottom:10,marginBottom:10,borderBottom:"1px solid var(--g100)"}}>
              <div style={{minWidth:0}}>
                <div style={{fontSize:11,color:"var(--g400)",fontWeight:500,marginBottom:2}}>{lang==="ar"?"الهامش":"Margin"}</div>
                <div style={{fontFamily:"var(--fm)",fontSize:18,fontWeight:700,color:marginColor,lineHeight:1.1}}>
                  {marginPct==null?"—":`${marginPct.toFixed(1)}%`}
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:11,color:"var(--g400)",fontWeight:500,marginBottom:2}}>{lang==="ar"?"الهدف":"Target"}</div>
                <div style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,color:"var(--ink)"}}>
                  {canEditTargets
                    ? <InlineNumEdit value={marginTargetPct} onSave={saveMarginTarget}
                        suffix="%" placeholder={lang==="ar"?"حدد":"Set %"}
                        ariaLabel={lang==="ar"?"تحديد هدف الهامش":"Set margin target"}
                        formatDisplay={(v)=>`${v}%`} min={0}/>
                    : (marginTargetPct==null?"—":`${marginTargetPct}%`)}
                </div>
              </div>
            </div>

            {/* Overall expense budget row */}
            <div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,marginBottom:8}}>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:11,color:"var(--g400)",fontWeight:500,marginBottom:2}}>{lang==="ar"?"إجمالي المصاريف":"Total Expenses"}</div>
                  <div style={{fontFamily:"var(--fm)",fontSize:18,fontWeight:700,color:overBudget?"var(--red)":"var(--ink)",lineHeight:1.1}}>
                    {fmtCompact(totalExpenses)}
                  </div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:11,color:"var(--g400)",fontWeight:500,marginBottom:2}}>{scaledLabel}</div>
                  <div style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,color:"var(--ink)"}}>
                    {canEditTargets
                      ? <InlineNumEdit value={expenseBudgetMonthly} onSave={saveExpenseBudget}
                          suffix={lang==="ar"?"ر.س/شهر":"SAR/mo"} placeholder={lang==="ar"?"حدد":"Set"}
                          ariaLabel={lang==="ar"?"تحديد الميزانية الشهرية":"Set monthly budget"}
                          formatDisplay={(v)=>fmtCompact(v)} min={0}/>
                      : (expenseBudgetMonthly==null?"—":`${fmtCompact(expenseBudgetMonthly)}/mo`)}
                  </div>
                </div>
              </div>
              {scaledExpenseBudget!=null && (
                <>
                  <div style={{height:6,background:"var(--g100)",borderRadius:3,overflow:"hidden"}}>
                    <div style={{width:`${barFillPct}%`,height:"100%",background:barColor,transition:"width .3s"}}/>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"var(--g400)",marginTop:4,fontFamily:"var(--fm)"}}>
                    <span>{fmtCompact(totalExpenses)} / {fmtCompact(scaledExpenseBudget)}</span>
                    <span style={{color:barColor,fontWeight:600}}>{expPct==null?"—":`${expPct.toFixed(0)}%`}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* Category breakdowns — side by side, Income analytics style */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:16,marginBottom:20}}>
        {(catDetails.length>0)&&(()=>{
          /* Show the Budget column when any row has a budget, or when
             the current user can edit (so they can reveal/set budgets). */
          const showBudgetCol = canEditTargets || catDetails.some(d=>d.monthlyBudget!=null);
          return (
            <div className="rep-cat-card">
              <div className="rep-cat-title">{lang==="ar"?"المصاريف حسب الفئة":"Expenses by Category"}</div>
              {catDetails.map(row=>{
                const d=compare&&prev?fmtDelta(row.actual,prev.byCat[row.name]||0):null;
                const scaledBudget = row.monthlyBudget!=null ? row.monthlyBudget*periodMonths : null;
                const variance = scaledBudget!=null && scaledBudget>0 ? (row.actual/scaledBudget)*100 : null;
                const over = scaledBudget!=null && row.actual>scaledBudget;
                return(
                  <div key={row.id||row.name} className="rep-cat-row" style={{display:"grid",gridTemplateColumns:showBudgetCol?"1fr auto auto auto":"1fr auto",gap:10,alignItems:"center"}}>
                    <div className="rep-cat-name">{row.name}</div>
                    <div style={{display:"flex",alignItems:"center",gap:6,justifyContent:"flex-end"}}>
                      {d&&<span style={{fontSize:10,fontWeight:600,color:d.color}}>{d.pct}</span>}
                      <div className="rep-cat-val" style={{color:"var(--red)"}}>{fmtCompact(row.actual)}</div>
                    </div>
                    {showBudgetCol && (
                      <div style={{fontSize:12,fontFamily:"var(--fm)",color:"var(--g500)",textAlign:"right",minWidth:70}}>
                        {row.id && canEditTargets
                          ? <InlineNumEdit value={row.monthlyBudget} onSave={(v)=>saveCatBudget(row.id,v)}
                              suffix={lang==="ar"?"/شهر":"/mo"} placeholder={lang==="ar"?"حدد":"Set"}
                              ariaLabel={(lang==="ar"?"تحديد ميزانية ":"Set budget for ")+row.name}
                              formatDisplay={(v)=>fmtCompact(v)} min={0}/>
                          : (scaledBudget==null ? <span style={{color:"var(--g300)"}}>—</span> : fmtCompact(scaledBudget))}
                      </div>
                    )}
                    {showBudgetCol && (
                      <div style={{fontSize:11,fontFamily:"var(--fm)",textAlign:"right",minWidth:44,fontWeight:600,color:variance==null?"var(--g300)":over?"var(--red)":variance>=85?"var(--amber,#c98f0d)":"var(--grn)"}}>
                        {variance==null?"—":`${variance.toFixed(0)}%`}
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="rep-cat-total">
                <div style={{fontSize:13,fontWeight:700,color:"var(--ink)",fontFamily:"var(--fi)"}}>{lang==="ar"?"الإجمالي":"Total"}</div>
                <div style={{fontFamily:"var(--fm)",fontSize:14,fontWeight:700,color:"var(--red)"}}>{fmtCompact(totalExpenses)}</div>
              </div>
            </div>
          );
        })()}

        {incCatRows.length>0&&(
          <div className="rep-cat-card">
            <div className="rep-cat-title">{lang==="ar"?"الدخل حسب الفئة":"Income by Category"}</div>
            {incCatRows.map(([name,val])=>{
              const d=compare&&prev?fmtDelta(val,prev.byIncCat[name]||0):null;
              return(
                <div key={name} className="rep-cat-row">
                  <div className="rep-cat-name">{name}</div>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    {d&&<span style={{fontSize:10,fontWeight:600,color:d.color}}>{d.pct}</span>}
                    <div className="rep-cat-val" style={{color:"var(--grn)"}}>{fmtCompact(val)}</div>
                  </div>
                </div>
              );
            })}
            <div className="rep-cat-total">
              <div style={{fontSize:13,fontWeight:700,color:"var(--ink)",fontFamily:"var(--fi)"}}>{lang==="ar"?"الإجمالي":"Total"}</div>
              <div style={{fontFamily:"var(--fm)",fontSize:14,fontWeight:700,color:"var(--grn)"}}>{fmtCompact(totalIncome)}</div>
            </div>
          </div>
        )}
      </div>

      {/* ── Visual Charts ── */}
      {(catRows.length>0||incCatRows.length>0)&&(()=>{
        const chartColors=reportChartColors(state.branding?.anchorColor);
        return(
          <div style={{marginBottom:20}}>
            {/* Income + Expense donut charts side by side on desktop, stacked on mobile */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:16,marginBottom:16}}>
              {/* Income Chart */}
              {incCatRows.length>0&&(
                <div className="rep-chart-card">
                  <div className="rep-cat-title">{lang==="ar"?"الدخل حسب الفئة":"Income by Category"}</div>
                  <ReportDonutChart data={incCatRows} colors={chartColors} total={totalIncome} label={lang==="ar"?"إجمالي الدخل":"Total Income"} totalColor="var(--grn)"/>
                  <ReportLegend data={incCatRows} colors={chartColors} total={totalIncome}/>
                </div>
              )}

              {/* Expense Chart */}
              {catRows.length>0&&(
                <div className="rep-chart-card">
                  <div className="rep-cat-title">{lang==="ar"?"المصاريف حسب الفئة":"Expenses by Category"}</div>
                  <ReportDonutChart data={catRows} colors={chartColors} total={totalExpenses} label={lang==="ar"?"إجمالي المصاريف":"Total Expenses"} totalColor="var(--red)"/>
                  <ReportLegend data={catRows} colors={chartColors} total={totalExpenses}/>
                </div>
              )}
            </div>

            {/* Sub-type breakdown — horizontal stacked bars */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:16}}>
              {/* Income by Sub-type */}
              {Object.keys(byIncSubType).length>0&&(
                <div className="rep-chart-card">
                  <div className="rep-cat-title">{lang==="ar"?"الدخل حسب النوع الفرعي":"Income by Sub-type"}</div>
                  <div style={{height:32,borderRadius:8,overflow:"hidden",display:"flex",marginBottom:14}}>
                    {Object.entries(byIncSubType).map(([st,val],i)=>{
                      const pct=totalIncome?(val/totalIncome*100):0;
                      return <div key={st} style={{width:`${pct}%`,background:chartColors[i%chartColors.length],minWidth:pct>0?2:0,transition:"width .4s"}} title={`${st}: ${fmtSAR(val)}`}/>;
                    })}
                  </div>
                  {Object.entries(byIncSubType).map(([st,val],i)=>{
                    const pct=totalIncome?(val/totalIncome*100):0;
                    return(
                      <div key={st} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderBottom:"1px solid rgba(0,0,0,.04)"}}>
                        <div style={{width:10,height:10,borderRadius:"50%",background:chartColors[i%chartColors.length],flexShrink:0}}/>
                        <div style={{flex:1,fontSize:13,color:"var(--ink)",fontFamily:"var(--fi)",fontWeight:500}}>{st}</div>
                        <div style={{fontSize:12,fontWeight:600,color:"var(--g500)",fontFamily:"var(--fm)"}}>{pct.toFixed(0)}%</div>
                        <div style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,minWidth:80,textAlign:"right"}}>{fmtCompact(val)}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Expenses by Sub-type */}
              {Object.keys(bySubType).length>0&&(
                <div className="rep-chart-card">
                  <div className="rep-cat-title">{lang==="ar"?"المصاريف حسب النوع الفرعي":"Expenses by Sub-type"}</div>
                  <div style={{height:32,borderRadius:8,overflow:"hidden",display:"flex",marginBottom:14}}>
                    {Object.entries(bySubType).map(([st,val],i)=>{
                      const pct=totalExpenses?(val/totalExpenses*100):0;
                      return <div key={st} style={{width:`${pct}%`,background:chartColors[i%chartColors.length],minWidth:pct>0?2:0,transition:"width .4s"}} title={`${st}: ${fmtSAR(val)}`}/>;
                    })}
                  </div>
                  {Object.entries(bySubType).map(([st,val],i)=>{
                    const pct=totalExpenses?(val/totalExpenses*100):0;
                    return(
                      <div key={st} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderBottom:"1px solid rgba(0,0,0,.04)"}}>
                        <div style={{width:10,height:10,borderRadius:"50%",background:chartColors[i%chartColors.length],flexShrink:0}}/>
                        <div style={{flex:1,fontSize:13,color:"var(--ink)",fontFamily:"var(--fi)",fontWeight:500}}>{st}</div>
                        <div style={{fontSize:12,fontWeight:600,color:"var(--g500)",fontFamily:"var(--fm)"}}>{pct.toFixed(0)}%</div>
                        <div style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,minWidth:80,textAlign:"right"}}>{fmtCompact(val)}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })()}
      <div className="rep-ai-wrap" style={{marginTop:8,marginBottom:0}}><AIPanel title={lang==="ar"?"ذكاء الأرباح والخسائر":"P&L Intelligence"} context={`Period: ${range.label}. Income: ${fmtSAR(totalIncome)}, Expenses: ${fmtSAR(totalExpenses)}, ${netProfit>=0?"Net Profit":"Net Loss"}: ${fmtSAR(Math.abs(netProfit))}, Margin: ${totalIncome>0?((netProfit/totalIncome)*100).toFixed(1):"0"}%. Top expense categories: ${catRows.slice(0,5).map(([n,v])=>`${n} ${fmtSAR(v)}`).join(", ")}`} prompt="Analyze this P&L. Cost concerns? Income gaps? 3 insights." lang={lang} moduleKey="Reports" wsState={state}/></div>
    </div>
  );
}

/* ══ BALANCE SHEET MODULE ══════════════════════════════════ */
function BalanceSheetModule({state,dispatch,t,activeTab,setActiveTab}) {
  const {balanceItems, holdings, vehicles, income, expenses, locations, lang} = state;
  const investmentLiabilities = state.investmentLiabilities || [];
  const isRTL = lang === "ar";
  const [bsMode,setBsMode]=useState("now"); /* now | year | custom */
  const [expandedCats,setExpandedCats]=useState({});
  const now=new Date();
  const [bsYear,setBsYear]=useState(now.getFullYear());
  const [bsDate,setBsDate]=useState(now.toISOString().slice(0,10));
  /* Only show years that have data */
  const dataYears=[...new Set([
    ...income.map(i=>i.date?.slice(0,4)),
    ...expenses.map(e=>e.date?.slice(0,4)),
    ...holdings.map(h=>h.entry?.slice(0,4)),
    String(now.getFullYear())
  ].filter(Boolean).map(Number))].sort((a,b)=>b-a);
  /* Snapshot date for filtering: "now" = today, "year" = Dec 31, "custom" = user-picked */
  const asOfDate=bsMode==="year"?`${bsYear}-12-31`:bsMode==="custom"?bsDate:now.toISOString().slice(0,10);
  const asOfLabel=bsMode==="year"?`Year ${bsYear}`:bsMode==="custom"?asOfDate:now.toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"});

  /* Forex conversion for multi-currency holdings + investment liabilities */
  const wsCur=state.currency||"SAR";
  const {fxRates,fxLoading,toWsCur}=useFxRates(holdings,investmentLiabilities,wsCur);

  const explicitAssets     = (balanceItems||[]).filter(b=>b.finType==="Asset");
  const explicitLiabilities= (balanceItems||[]).filter(b=>b.finType==="Liability");
  const totalInvestments = holdings.reduce((a,h)=>a+toWsCur(h.currentVal,h.currency), 0);
  const holdingRows = holdings.map(h=>({id:h.id,name:h.name,finType:"Asset",subType:"Investment",amount:toWsCur(h.currentVal,h.currency),category:h.type,notes:h.ticker?`Ticker: ${h.ticker}`:""}));
  const vehicleRows = vehicles.map(v=>({id:v.id,name:`${v.year} ${v.make} ${v.model}`,finType:"Asset",subType:"Operating",amount:v.currentVal||0,category:"Vehicle",notes:v.plate}));
  const expenseAssets = expenses.filter(e=>e.finType==="Asset"&&(!e.date||e.date<=asOfDate));
  const invLiabRows = investmentLiabilities.filter(l=>l.status==="active").map(l=>({id:l.id,name:l.name,finType:"Liability",subType:"Financing",amount:toWsCur(l.outstandingBalance||l.principal||0,l.currency),category:"Investment Liability",notes:l.lender?`Lender: ${l.lender}`:""}));
  const allAssets = [...explicitAssets,...holdingRows,...vehicleRows,...expenseAssets.map(e=>({id:e.id,name:e.notes||"Asset Purchase",finType:"Asset",subType:e.subType||"Operating",amount:e.amount,category:"Other",notes:e.date}))];
  const allLiabilities = [...explicitLiabilities, ...invLiabRows];
  const totalAssets = allAssets.reduce((a,item)=>a+item.amount, 0);
  const totalLiabilities = allLiabilities.reduce((a,item)=>a+item.amount, 0);
  const equity = totalAssets - totalLiabilities;
  const assetsByCategory = {};
  allAssets.forEach(item=>{const cat=item.category||"Other";if(!assetsByCategory[cat])assetsByCategory[cat]=[];assetsByCategory[cat].push(item);});
  const liabsByCategory = {};
  allLiabilities.forEach(item=>{const cat=item.category||"Other";if(!liabsByCategory[cat])liabsByCategory[cat]=[];liabsByCategory[cat].push(item);});

  /* Shared card renderer */
  const toggleCat=(cat)=>setExpandedCats(p=>({...p,[cat]:!p[cat]}));
  const renderGroup=(entries,color,bgVar,bdVar)=>entries.map(([cat,items],idx)=>{
    const catTotal=items.reduce((a,i)=>a+i.amount,0);
    const isOpen=!!expandedCats[cat];
    return(
      <div key={cat} className="card" style={{marginBottom:12,flex:idx===entries.length-1?"1":"none",display:"flex",flexDirection:"column"}}>
        <div className="ch" style={{cursor:"pointer"}} onClick={()=>toggleCat(cat)}>
          <div style={{display:"flex",alignItems:"center",gap:8,flex:1}}>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" style={{transition:"transform .15s",transform:isOpen?"rotate(90deg)":"rotate(0deg)"}}><path d="M3 1l4 4-4 4"/></svg>
            <div className="ct">{cat}</div>
            <span style={{fontSize:11,color:"var(--g400)",fontWeight:400}}>({items.length})</span>
          </div>
          <span style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,color}}>{fmtSAR(catTotal)}</span>
        </div>
        {isOpen&&<div>
          {items.map(item=>(
            <div key={item.id} style={{display:"flex",alignItems:"center",padding:"9px 16px",borderBottom:"1px solid var(--g100)",gap:8}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:500,letterSpacing:"-.01em",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{localName(item, lang)}</div>
                {item.notes&&<div style={{fontSize:11,color:"var(--g400)"}}>{item.notes}</div>}
              </div>
              <span style={{fontFamily:"var(--fm)",fontSize:12,fontWeight:600,color,whiteSpace:"nowrap"}}>{fmtSAR(item.amount)}</span>
            </div>
          ))}
        </div>}
      </div>
    );
  });

  return (
    <div className="page rep-page-bg">
      {/* Desktop header */}
      <div className="ph rep-hero-desktop" style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div>
          <div className="ptitle">{lang==="ar"?"الميزانية العمومية":"Balance Sheet"}</div>
          <div className="psub">{lang==="ar"?"بيان المركز المالي":"Statement of Financial Position"} · {lang==="ar"?"كما في":"As of"} {asOfLabel}</div>
        </div>
        <button className="btn btn-s btn-sm" style={{gap:6,fontSize:12}} onClick={()=>{
          const fS=fmtSAR;
          const assetEntries=Object.entries(assetsByCategory);
          const liabEntries=Object.entries(liabsByCategory);
          generateReportPDF({title:"Balance Sheet",subtitle:`Statement of Financial Position · As of ${asOfLabel}`,state,lang,sections:[
            {kpis:[
              {label:"Total Assets",value:fS(totalAssets),color:"green"},
              {label:"Total Liabilities",value:fS(totalLiabilities),color:totalLiabilities>0?"red":"default"},
              {label:"Net Equity",value:fS(equity),color:equity>=0?"green":"red"},
              {label:"Debt Ratio",value:totalAssets>0?`${((totalLiabilities/totalAssets)*100).toFixed(1)}%`:"—"}
            ]},
            {heading:"Assets",table:{
              headers:["Item","Category","Amount"],
              rows:allAssets.map(a=>[localName(a, lang),a.category||"Other",fS(a.amount)]),
              totals:["Total Assets","",fS(totalAssets)]
            }},
            {heading:"Liabilities",table:{
              headers:["Item","Category","Amount"],
              rows:allLiabilities.map(l=>[localName(l, lang),l.category||"Other",fS(l.amount)]),
              totals:["Total Liabilities","",fS(totalLiabilities)]
            }},
            {heading:"Equity",rows:[
              {label:"Total Assets",value:"+"+fS(totalAssets),color:"green"},
              {label:"Total Liabilities",value:"-"+fS(totalLiabilities),color:"red"},
            ]},
            {summary:{label:"Net Equity",value:fS(equity)}}
          ]});
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 18 15 15"/></svg>
          PDF
        </button>
      </div>

      {/* Mobile hero header */}
      <div className="rep-hero">
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div className="rep-hero-greeting">{lang==="ar"?"الميزانية العمومية":"Balance Sheet"}</div>
          <button className="btn btn-s btn-sm" style={{gap:4,fontSize:11,padding:"6px 10px"}} onClick={()=>{
            const fS=fmtSAR;
            generateReportPDF({title:"Balance Sheet",subtitle:`Statement of Financial Position · As of ${asOfLabel}`,state,lang,sections:[
              {kpis:[{label:"Total Assets",value:fS(totalAssets),color:"green"},{label:"Total Liabilities",value:fS(totalLiabilities),color:totalLiabilities>0?"red":"default"},{label:"Net Equity",value:fS(equity),color:equity>=0?"green":"red"},{label:"Debt Ratio",value:totalAssets>0?`${((totalLiabilities/totalAssets)*100).toFixed(1)}%`:"—"}]},
              {heading:"Assets",table:{headers:["Item","Category","Amount"],rows:allAssets.map(a=>[localName(a, lang),a.category||"Other",fS(a.amount)]),totals:["Total Assets","",fS(totalAssets)]}},
              {heading:"Liabilities",table:{headers:["Item","Category","Amount"],rows:allLiabilities.map(l=>[localName(l, lang),l.category||"Other",fS(l.amount)]),totals:["Total Liabilities","",fS(totalLiabilities)]}},
              {heading:"Equity",rows:[{label:"Total Assets",value:"+"+fS(totalAssets),color:"green"},{label:"Total Liabilities",value:"-"+fS(totalLiabilities),color:"red"}]},
              {summary:{label:"Net Equity",value:fS(equity)}}
            ]});
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 18 15 15"/></svg>
            PDF
          </button>
        </div>
        <div className="rep-hero-sub">{lang==="ar"?"كما في":"As of"} {asOfLabel}</div>
        <ReportTabSwitcher activeTab={activeTab} setActiveTab={setActiveTab}/>
        <div className="rep-summary-glass">
          <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:10}}>
            <div style={{fontFamily:"var(--fm)",fontSize:22,fontWeight:600,color:equity>=0?"var(--grn)":"var(--red)",letterSpacing:"-.02em",lineHeight:1}}>{fmtCompact(equity)}</div>
            <div style={{fontSize:11,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".06em"}}>{lang==="ar"?"صافي حقوق الملكية":"net equity"}</div>
            {fxLoading&&<div style={{fontSize:10,color:"var(--amb)",fontWeight:500,marginInlineStart:4}}>{lang==="ar"?"تحديث الأسعار…":"updating rates…"}</div>}
          </div>
          <div style={{display:"flex",justifyContent:"space-between",gap:8}}>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:"var(--grn)"}}/>
              <div><div style={{fontSize:10,color:"var(--g400)",fontWeight:500,lineHeight:1}}>{lang==="ar"?"الأصول":"Assets"}</div><div style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,color:"var(--grn)",lineHeight:1.3}}>{fmtCompact(totalAssets)}</div></div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:"var(--red)"}}/>
              <div><div style={{fontSize:10,color:"var(--g400)",fontWeight:500,lineHeight:1}}>{lang==="ar"?"الالتزامات":"Liabilities"}</div><div style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,color:"var(--red)",lineHeight:1.3}}>{fmtCompact(totalLiabilities)}</div></div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:"var(--g300)"}}/>
              <div><div style={{fontSize:10,color:"var(--g400)",fontWeight:500,lineHeight:1}}>{lang==="ar"?"نسبة الدين":"Debt Ratio"}</div><div style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,lineHeight:1.3}}>{totalAssets>0?`${((totalLiabilities/totalAssets)*100).toFixed(1)}%`:"—"}</div></div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop: tabs row */}
      <div className="rep-hero-desktop" style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
        <div><ReportTabSwitcher activeTab={activeTab} setActiveTab={setActiveTab}/></div>
      </div>

      {/* Date picker */}
      <div className="rep-period-card">
        <div style={{display:"flex",gap:6,width:"100%"}}>
          {[{k:"now",l:lang==="ar"?"حالي":"Current"},{k:"year",l:lang==="ar"?"حسب السنة":"By Year"},{k:"custom",l:lang==="ar"?"حسب التاريخ":"As of Date"}].map(m=>(
            <button key={m.k} className={`btn btn-sm ${bsMode===m.k?"btn-p":"btn-s"}`} style={{flex:1,justifyContent:"center",textAlign:"center"}} onClick={()=>setBsMode(m.k)}>{m.l}</button>
          ))}
        </div>
        {bsMode==="year"&&<div style={{marginTop:12,width:"100%"}}><select className="fs" value={bsYear} onChange={e=>setBsYear(parseInt(e.target.value))} style={{width:"100%",fontSize:13,padding:"8px 12px"}}>{dataYears.map(y=><option key={y} value={y}>{y}</option>)}</select></div>}
        {bsMode==="custom"&&<div style={{marginTop:12,width:"100%"}}><input className="fi" type="date" value={bsDate} onChange={e=>setBsDate(e.target.value)} style={{width:"100%",fontSize:13,padding:"8px 12px",boxSizing:"border-box"}}/></div>}
      </div>


      {/* Empty state guidance */}
      {allAssets.length===0&&allLiabilities.length===0&&(
        <div className="rep-cat-card" style={{padding:24,textAlign:"center",marginBottom:20}}>
          <div style={{fontSize:28,marginBottom:8,opacity:0.3}}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{display:"inline"}}><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
          </div>
          <div style={{fontSize:15,fontWeight:600,color:"var(--ink)",marginBottom:6,fontFamily:"var(--fi)"}}>{lang==="ar"?"لا توجد أصول أو التزامات":"No assets or liabilities yet"}</div>
          <div style={{fontSize:13,color:"var(--g400)",lineHeight:1.5,maxWidth:360,margin:"0 auto"}}>{lang==="ar"?"أضف استثمارات من وحدة الاستثمارات، أو مركبات من وحدة الأسطول، أو سجّل أصولاً ومطلوبات مباشرة لتظهر الميزانية العمومية.":"Add investments, vehicles, or record assets and liabilities directly to see your Balance Sheet here."}</div>
        </div>
      )}

      {/* Assets & Liabilities side-by-side with equal heights */}
      <div className="g2" style={{gap:20,marginBottom:20,alignItems:"stretch"}}>
        <div style={{display:"flex",flexDirection:"column"}}>
          <div style={{fontSize:11,fontWeight:600,color:"var(--g500)",textTransform:"uppercase",letterSpacing:".08em",marginBottom:10}}>{t.assets}</div>
          <div style={{flex:1,display:"flex",flexDirection:"column"}}>
            {Object.entries(assetsByCategory).length===0&&<div className="card" style={{marginBottom:12,flex:1}}><div className="empty">{t.noAssetsRecorded}</div></div>}
            {renderGroup(Object.entries(assetsByCategory),"var(--grn)","var(--grn-bg)","var(--grn-bd)")}
          </div>
          <div style={{background:"var(--ink)",color:"white",borderRadius:"var(--r2)",padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:13,fontWeight:600}}>{t.totalAssets}</span>
            <span style={{fontFamily:"var(--fm)",fontSize:18,fontWeight:400,letterSpacing:"-.02em"}}>{fmtSAR(totalAssets)}</span>
          </div>
        </div>
        <div style={{display:"flex",flexDirection:"column"}}>
          <div style={{fontSize:11,fontWeight:600,color:"var(--g500)",textTransform:"uppercase",letterSpacing:".08em",marginBottom:10}}>{t.liabilities}</div>
          <div style={{flex:1,display:"flex",flexDirection:"column"}}>
            {allLiabilities.length===0&&<div className="card" style={{marginBottom:12,flex:1}}><div className="empty">{t.noLiabilitiesRecorded}</div></div>}
            {renderGroup(Object.entries(liabsByCategory),"var(--red)","var(--red-bg)","var(--red-bd)")}
          </div>
          <div style={{background:allLiabilities.length>0?"var(--red)":"var(--g200)",color:"white",borderRadius:"var(--r2)",padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:13,fontWeight:600}}>{t.totalLiabilities}</span>
            <span style={{fontFamily:"var(--fm)",fontSize:18,fontWeight:400,letterSpacing:"-.02em"}}>{fmtSAR(totalLiabilities)}</span>
          </div>
        </div>
      </div>

      {/* Equity — full width below */}
      <div style={{marginBottom:20}}>
        <div style={{fontSize:11,fontWeight:600,color:"var(--g500)",textTransform:"uppercase",letterSpacing:".08em",marginBottom:10}}>{t.equity}</div>
        <div className="card" style={{marginBottom:12}}>
          <div style={{display:"flex",justifyContent:"space-between",padding:"10px 18px",borderBottom:"1px solid var(--g100)"}}><span style={{fontSize:13,color:"var(--g600)"}}>{t.totalAssets}</span><span style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,color:"var(--grn)"}}>+{fmtSAR(totalAssets)}</span></div>
          <div style={{display:"flex",justifyContent:"space-between",padding:"10px 18px"}}><span style={{fontSize:13,color:"var(--g600)"}}>{t.totalLiabilities}</span><span style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,color:"var(--red)"}}>-{fmtSAR(totalLiabilities)}</span></div>
        </div>
        <div style={{background:equity>=0?"var(--grn)":"var(--red)",color:"white",borderRadius:"var(--r2)",padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:13,fontWeight:600}}>{t.netEquity}</span>
          <span style={{fontFamily:"var(--fm)",fontSize:18,fontWeight:400,letterSpacing:"-.02em"}}>{fmtSAR(equity)}</span>
        </div>
      </div>

      <div className="rep-ai-wrap" style={{marginTop:8,marginBottom:0}}><AIPanel title={lang==="ar"?"ذكاء الميزانية العمومية":"Balance Sheet Intelligence"} context={`As of ${asOfLabel}. Total Assets: ${fmtSAR(totalAssets)} (Investments: ${fmtSAR(totalInvestments)}, Vehicles: ${fmtSAR(vehicles.reduce((a,v)=>a+(v.currentVal||0),0))}, Other: ${fmtSAR(explicitAssets.reduce((a,i)=>a+i.amount,0))}). Total Liabilities: ${fmtSAR(totalLiabilities)} (Investment Liabilities: ${fmtSAR(invLiabRows.reduce((a,l)=>a+l.amount,0))}, Other: ${fmtSAR(explicitLiabilities.reduce((a,i)=>a+i.amount,0))}). Net Equity: ${fmtSAR(equity)}. Debt Ratio: ${totalAssets>0?((totalLiabilities/totalAssets)*100).toFixed(1):"0"}%`} prompt="Analyze balance sheet. Asset concentration? Debt level? Equity trend? 3 insights." lang={lang} moduleKey="Reports" wsState={state}/></div>
    </div>
  );
}

/* Temp modal for adding balance sheet items — dispatch via window ref */
function AddBalanceItemModal({locations,lang,t,onClose,onSave}) {
  const [f,setF]=useState({name:"",finType:"Asset",subType:"Investment",amount:"",date:TODAY,locId:"",vehicleId:"",category:"",notes:""});
  const sf=(k,v)=>setF(p=>({...p,[k]:v}));
  return(
    <div className="mo" onClick={onClose}>
      <div className="md" onClick={e=>e.stopPropagation()}>
        <div className="mh"><div className="mt">{lang==="ar"?"إضافة أصل / خصم":"Add Asset / Liability"}</div><button className="btn btn-g btn-sm" onClick={onClose}>✕</button></div>
        <div className="mb">
          <div className="fr">
            <div className="fg" style={{flex:2}}><label className="fl">{t.name}</label><input className="fi" value={f.name} onChange={e=>sf("name",e.target.value)} autoFocus/></div>
            <div className="fg"><label className="fl">{t.type}</label><select className="fs" value={f.finType} onChange={e=>sf("finType",e.target.value)}><option value="Asset">{t.asset}</option><option value="Liability">{t.liability}</option></select></div>
          </div>
          <div className="fr">
            <div className="fg"><label className="fl">{t.subType}</label><select className="fs" value={f.subType} onChange={e=>sf("subType",e.target.value)}><option value="Operating">{t.operating}</option><option value="Investment">{t.investment}</option><option value="Financing">{t.financing}</option></select></div>
            <div className="fg"><label className="fl">{t.category}</label><input className="fi" value={f.category} onChange={e=>sf("category",e.target.value)}/></div>
          </div>
          <div className="fr">
            <div className="fg"><label className="fl">{t.amount}</label><input className="fi" type="number" value={f.amount} onChange={e=>sf("amount",e.target.value)}/></div>
            <div className="fg"><label className="fl">{t.date}</label><input className="fi" type="date" value={f.date} onChange={e=>sf("date",e.target.value)}/></div>
          </div>
          <div className="fg" style={{marginBottom:12}}><label className="fl">{t.location} ({t.optional})</label><select className="fs" value={f.locId} onChange={e=>sf("locId",e.target.value)}><option value="">{t.global||"Global"}</option>{locations.map(l=><option key={l.id} value={l.id}>{localName(l, lang)}</option>)}</select></div>
          <div className="fg"><label className="fl">{t.notes}</label><input className="fi" value={f.notes} onChange={e=>sf("notes",e.target.value)} placeholder={lang==="ar"?"تفاصيل اختيارية…":"Optional details…"}/></div>
        </div>
        <div className="mf">
          <button className="btn btn-g" onClick={onClose}>{t.cancel}</button>
          <button className="btn btn-p" onClick={()=>{if(f.name&&f.amount){onSave({...f,id:`bs${Date.now()}`,amount:parseFloat(f.amount)||0});}}}>{t.add}</button>
        </div>
      </div>
    </div>
  );
}



/* ══ CASH FLOW MODULE ═════════════════════════════════════ */
function CashFlowModule({state,dispatch,t,activeTab,setActiveTab}){
  const {income,expenses,recurringExpenses,recurringIncome,expenseCats,incomeCats,holdings,locations,lang}=state;
  const investmentLiabilities=state.investmentLiabilities||[];
  const wsCur=state.currency||"SAR";
  const {fxRates,fxLoading,toWsCur}=useFxRates(holdings,investmentLiabilities,wsCur);

  const [expandedCF,setExpandedCF]=useState({});
  const toggleCF=(cat)=>setExpandedCF(p=>({...p,[cat]:!p[cat]}));

  /* ── Period picker (shared) ── */
  const period=useReportPeriod(state.locFilter);
  const {range,filterLoc}=period;

  /* ── All cash flow computations (memoized) ── */
  const cf=useMemo(()=>{
    /* Operating Activities */
    const opInflows=income.filter(i=>{
      const inP=inRange(i.date,range);const locM=!filterLoc||!i.locId||i.locId===filterLoc;
      return inP&&locM&&(!i.finType||i.finType==="Income")&&i.status==="Realised";
    });
    const totalOpIn=opInflows.reduce((a,i)=>a+i.amount,0);
    const opOutflows=expenses.filter(e=>{
      const inP=inRange(e.date,range);const locM=!filterLoc||!e.locId||e.locId===filterLoc;
      return inP&&locM&&(!e.finType||e.finType==="Expense");
    });
    const totalOpOut=opOutflows.reduce((a,e)=>a+e.amount,0);
    const netOperating=totalOpIn-totalOpOut;
    /* Inflow by parent category */
    const _byInflowCat={};
    opInflows.forEach(i=>{
      const cat=(incomeCats||[]).find(c=>c.id===i.catId);
      const par=cat?.parent?(incomeCats||[]).find(c=>c.id===cat.parent):cat;
      const r=par||cat;const name=r?(localName(r, lang)):"Uncategorised";
      _byInflowCat[name]=(_byInflowCat[name]||0)+i.amount;
    });
    const inflowCatRows=Object.entries(_byInflowCat).sort((a,b)=>b[1]-a[1]);
    /* Outflow by parent category */
    const _byOutflowCat={};
    opOutflows.forEach(e=>{
      const cat=expenseCats.find(c=>c.id===e.catId);
      const par=cat?.parent?expenseCats.find(c=>c.id===cat.parent):cat;
      const r=par||cat;const name=r?(localName(r, lang)):"Uncategorised";
      _byOutflowCat[name]=(_byOutflowCat[name]||0)+e.amount;
    });
    const outflowCatRows=Object.entries(_byOutflowCat).sort((a,b)=>b[1]-a[1]);
    /* Investing Activities */
    const assetPurchases=expenses.filter(e=>{
      const inP=inRange(e.date,range);const locM=!filterLoc||!e.locId||e.locId===filterLoc;
      return inP&&locM&&e.finType==="Asset";
    });
    const totalAssetPurchases=assetPurchases.reduce((a,e)=>a+e.amount,0);
    const newInvestments=(holdings||[]).filter(h=>inRange(h.entry,range));
    const totalNewInvestments=newInvestments.reduce((a,h)=>a+toWsCur(h.invested||0,h.currency),0);
    const netInvesting=-(totalAssetPurchases+totalNewInvestments);
    /* Financing Activities */
    const activeLiabs=investmentLiabilities.filter(l=>l.status==="active");
    const monthlyLiabs=activeLiabs.filter(l=>l.paymentFrequency!=="bullet");
    const bulletLiabs=activeLiabs.filter(l=>l.paymentFrequency==="bullet");
    const monthlyDebtService=monthlyLiabs.reduce((a,l)=>a+toWsCur(l.monthlyPayment||0,l.currency),0);
    const bulletTotal=bulletLiabs.reduce((a,l)=>a+toWsCur(l.outstandingBalance||0,l.currency),0);
    const periodMonths=Math.max(1,Math.round((range.end-range.start)/(30.44*24*60*60*1000)));
    const periodDebtPayments=monthlyDebtService*periodMonths;
    const netFinancing=-periodDebtPayments;
    /* Net Cash Flow */
    const netCashFlow=netOperating+netInvesting+netFinancing;
    const totalCashIn=totalOpIn;
    const totalCashOut=totalOpOut+totalAssetPurchases+totalNewInvestments+periodDebtPayments;
    const inBar=(totalCashIn+totalCashOut)>0?Math.max(totalCashIn/(totalCashIn+totalCashOut)*100,2):50;
    /* Recurring projections */
    const monthlyRecurInc=(recurringIncome||[]).filter(r=>r.active&&r.frequency==="Monthly").reduce((a,r)=>a+r.amount,0);
    const monthlyRecurExp=(recurringExpenses||[]).filter(r=>r.active&&r.frequency==="Monthly").reduce((a,r)=>a+r.amount,0);
    const annualRecurInc=(recurringIncome||[]).reduce((a,r)=>{if(!r.active)return a;return a+r.amount*({Monthly:12,Quarterly:4,Yearly:1}[r.frequency]||12);},0);
    const annualRecurExp=(recurringExpenses||[]).reduce((a,r)=>{if(!r.active)return a;return a+r.amount*({Monthly:12,Quarterly:4,Yearly:1}[r.frequency]||12);},0);
    const netMonthlyRecur=monthlyRecurInc-monthlyRecurExp-monthlyDebtService;
    const netAnnualProjection=annualRecurInc-annualRecurExp-(monthlyDebtService*12);
    /* Health indicators */
    const burnRate=totalOpOut>0?totalOpOut/periodMonths:monthlyRecurExp+monthlyDebtService;
    const _invCats=state.investmentCategories||[];
    const hasLiquidityCfg=_invCats.some(c=>typeof c==="object"&&c.liquidity);
    const liquidAssets=(holdings||[]).filter(h=>{
      if(!hasLiquidityCfg)return true;
      const cat=_invCats.find(c=>(typeof c==="string"?c:c.name)===h.type);
      return cat&&typeof cat==="object"&&cat.liquidity==="High";
    }).reduce((a,h)=>a+toWsCur(h.currentVal,h.currency),0);
    const cashRunway=burnRate>0?liquidAssets/burnRate:null;
    const savingsRate=totalOpIn>0?netOperating/totalOpIn:0;
    const debtServiceCoverage=monthlyDebtService>0?(monthlyRecurInc||totalOpIn/periodMonths)/monthlyDebtService:null;
    return{totalOpIn,totalOpOut,netOperating,inflowCatRows,outflowCatRows,assetPurchases,totalAssetPurchases,newInvestments,totalNewInvestments,netInvesting,monthlyLiabs,bulletLiabs,monthlyDebtService,bulletTotal,periodMonths,periodDebtPayments,netFinancing,netCashFlow,totalCashIn,totalCashOut,inBar,outBar:100-inBar,monthlyRecurInc,monthlyRecurExp,annualRecurInc,annualRecurExp,netMonthlyRecur,netAnnualProjection,burnRate,hasLiquidityCfg,liquidAssets,cashRunway,savingsRate,debtServiceCoverage};
  },[income,expenses,expenseCats,incomeCats,holdings,investmentLiabilities,recurringIncome,recurringExpenses,range,filterLoc,lang,toWsCur,state.investmentCategories]);
  const {totalOpIn,totalOpOut,netOperating,inflowCatRows,outflowCatRows,assetPurchases,totalAssetPurchases,newInvestments,totalNewInvestments,netInvesting,monthlyLiabs,bulletLiabs,monthlyDebtService,bulletTotal,periodMonths,periodDebtPayments,netFinancing,netCashFlow,totalCashIn,totalCashOut,inBar,outBar,monthlyRecurInc,monthlyRecurExp,annualRecurInc,annualRecurExp,netMonthlyRecur,netAnnualProjection,burnRate,hasLiquidityCfg,liquidAssets,cashRunway,savingsRate,debtServiceCoverage}=cf;

  return(
    <div className="page rep-page-bg">
      {/* Desktop header */}
      <div className="ph rep-hero-desktop" style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div>
          <div className="ptitle">{lang==="ar"?"التدفق النقدي":"Cash Flow"}</div>
          <div className="psub">{lang==="ar"?"بيان التدفق النقدي":"Cash Flow Statement"} · {range.label}{filterLoc?" · "+locName(locations,filterLoc):""}</div>
        </div>
        <button className="btn btn-s btn-sm" style={{gap:6,fontSize:12}} onClick={()=>{
          const fS=fmtSAR;const fC=fmtCompact;
          generateReportPDF({title:"Cash Flow Statement",subtitle:`${range.label}${filterLoc?" · "+locName(locations,filterLoc):""}`,state,lang,sections:[
            {kpis:[
              {label:"Cash Inflows",value:fC(totalOpIn),color:"green"},
              {label:"Cash Outflows",value:fC(totalOpOut),color:"red"},
              {label:"Net Operating",value:(netOperating>=0?"+":"")+fC(netOperating),color:netOperating>=0?"green":"red"},
              {label:"Net Cash Flow",value:(netCashFlow>=0?"+":"")+fC(netCashFlow),color:netCashFlow>=0?"green":"red"}
            ]},
            {heading:"Operating Activities — Inflows",table:{
              headers:["Category","Amount"],
              rows:inflowCatRows.map(([n,v])=>[n,fS(v)]),
              totals:["Total Inflows",fS(totalOpIn)]
            }},
            ...(inflowCatRows.length>0?[{bars:{items:inflowCatRows.map(([n,v])=>({label:n,value:v,formatted:fS(v)})),total:totalOpIn,barColor:"#228B22"}}]:[]),
            {heading:"Operating Activities — Outflows",table:{
              headers:["Category","Amount"],
              rows:outflowCatRows.map(([n,v])=>[n,fS(v)]),
              totals:["Total Outflows",fS(totalOpOut)]
            }},
            ...(outflowCatRows.length>0?[{bars:{items:outflowCatRows.map(([n,v])=>({label:n,value:v,formatted:fS(v)})),total:totalOpOut,barColor:"#C82828"}}]:[]),
            {heading:"Investing Activities",rows:[
              ...(totalAssetPurchases>0?[{label:"Asset Purchases",value:"-"+fS(totalAssetPurchases),color:"red"}]:[]),
              ...(totalNewInvestments>0?[{label:"New Investments",value:"-"+fS(totalNewInvestments),color:"red"}]:[]),
              ...newInvestments.map(h=>({label:"  "+h.name,value:fS(toWsCur(h.invested||0,h.currency))})),
              {label:"Net Investing",value:(netInvesting>=0?"+":"")+fS(netInvesting),color:netInvesting>=0?"green":"red"}
            ]},
            {heading:"Financing Activities",rows:[
              ...(periodDebtPayments>0?[{label:`Debt Repayments (${periodMonths} mo)`,value:"-"+fS(periodDebtPayments),color:"red"}]:[]),
              ...(monthlyDebtService>0?[{label:"Monthly Debt Service",value:fS(monthlyDebtService)}]:[]),
              ...(bulletTotal>0?[{label:"Balloon Payments Due",value:fS(bulletTotal),color:"red"}]:[]),
              {label:"Net Financing",value:(netFinancing>=0?"+":"")+fS(netFinancing),color:netFinancing>=0?"green":"red"}
            ]},
            {heading:"Cash Flow Summary",rows:[
              {label:"Operating Activities",value:(netOperating>=0?"+":"")+fS(netOperating),color:netOperating>=0?"green":"red"},
              {label:"Investing Activities",value:(netInvesting>=0?"+":"")+fS(netInvesting),color:netInvesting>=0?"green":"red"},
              {label:"Financing Activities",value:(netFinancing>=0?"+":"")+fS(netFinancing),color:netFinancing>=0?"green":"red"},
            ]},
            {summary:{label:"Net Cash Flow",value:(netCashFlow>=0?"+":"")+fS(netCashFlow)}},
            {heading:"Recurring Projection",kpis:[
              ...(monthlyRecurInc>0?[{label:"Monthly Income",value:fC(monthlyRecurInc),color:"green"}]:[]),
              {label:"Monthly Expenses",value:fC(monthlyRecurExp),color:"red"},
              {label:"Net Monthly",value:(netMonthlyRecur>=0?"+":"")+fC(netMonthlyRecur),color:netMonthlyRecur>=0?"green":"red"},
            ]},
            {heading:"Health Indicators",kpis:[
              {label:"Savings Rate",value:`${(savingsRate*100).toFixed(0)}%`,color:savingsRate>=0.2?"green":savingsRate>=0?"default":"red"},
              {label:"Burn Rate",value:fC(burnRate)+"/mo",color:"red"},
              {label:"Debt Coverage",value:debtServiceCoverage!==null?`${debtServiceCoverage.toFixed(1)}x`:"—",color:debtServiceCoverage!==null&&debtServiceCoverage>=1.5?"green":debtServiceCoverage!==null&&debtServiceCoverage>=1?"default":"red"},
              {label:"Cash Runway",value:cashRunway!==null?`${cashRunway.toFixed(0)} mo`:"—",color:cashRunway!==null&&cashRunway>=12?"green":cashRunway!==null&&cashRunway>=6?"default":"red"}
            ]}
          ]});
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 18 15 15"/></svg>
          PDF
        </button>
      </div>

      {/* Mobile hero header */}
      <div className="rep-hero">
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div className="rep-hero-greeting">{lang==="ar"?"التدفق النقدي":"Cash Flow"}</div>
          <button className="btn btn-s btn-sm" style={{gap:4,fontSize:11,padding:"6px 10px"}} onClick={()=>{
            const fS=fmtSAR;const fC=fmtCompact;
            generateReportPDF({title:"Cash Flow Statement",subtitle:`${range.label}${filterLoc?" · "+locName(locations,filterLoc):""}`,state,lang,sections:[
              {kpis:[{label:"Cash Inflows",value:fC(totalOpIn),color:"green"},{label:"Cash Outflows",value:fC(totalOpOut),color:"red"},{label:"Net Operating",value:(netOperating>=0?"+":"")+fC(netOperating),color:netOperating>=0?"green":"red"},{label:"Net Cash Flow",value:(netCashFlow>=0?"+":"")+fC(netCashFlow),color:netCashFlow>=0?"green":"red"}]},
              {heading:"Operating Activities — Inflows",table:{headers:["Category","Amount"],rows:inflowCatRows.map(([n,v])=>[n,fS(v)]),totals:["Total Inflows",fS(totalOpIn)]}},
              ...(inflowCatRows.length>0?[{bars:{items:inflowCatRows.map(([n,v])=>({label:n,value:v,formatted:fS(v)})),total:totalOpIn,barColor:"#228B22"}}]:[]),
              {heading:"Operating Activities — Outflows",table:{headers:["Category","Amount"],rows:outflowCatRows.map(([n,v])=>[n,fS(v)]),totals:["Total Outflows",fS(totalOpOut)]}},
              ...(outflowCatRows.length>0?[{bars:{items:outflowCatRows.map(([n,v])=>({label:n,value:v,formatted:fS(v)})),total:totalOpOut,barColor:"#C82828"}}]:[]),
              {heading:"Investing Activities",rows:[...(totalAssetPurchases>0?[{label:"Asset Purchases",value:"-"+fS(totalAssetPurchases),color:"red"}]:[]),...(totalNewInvestments>0?[{label:"New Investments",value:"-"+fS(totalNewInvestments),color:"red"}]:[]),...newInvestments.map(h=>({label:"  "+h.name,value:fS(toWsCur(h.invested||0,h.currency))})),{label:"Net Investing",value:(netInvesting>=0?"+":"")+fS(netInvesting),color:netInvesting>=0?"green":"red"}]},
              {heading:"Financing Activities",rows:[...(periodDebtPayments>0?[{label:`Debt Repayments (${periodMonths} mo)`,value:"-"+fS(periodDebtPayments),color:"red"}]:[]),...(monthlyDebtService>0?[{label:"Monthly Debt Service",value:fS(monthlyDebtService)}]:[]),...(bulletTotal>0?[{label:"Balloon Payments Due",value:fS(bulletTotal),color:"red"}]:[]),{label:"Net Financing",value:(netFinancing>=0?"+":"")+fS(netFinancing),color:netFinancing>=0?"green":"red"}]},
              {heading:"Cash Flow Summary",rows:[{label:"Operating Activities",value:(netOperating>=0?"+":"")+fS(netOperating),color:netOperating>=0?"green":"red"},{label:"Investing Activities",value:(netInvesting>=0?"+":"")+fS(netInvesting),color:netInvesting>=0?"green":"red"},{label:"Financing Activities",value:(netFinancing>=0?"+":"")+fS(netFinancing),color:netFinancing>=0?"green":"red"}]},
              {summary:{label:"Net Cash Flow",value:(netCashFlow>=0?"+":"")+fS(netCashFlow)}},
              {heading:"Health Indicators",kpis:[{label:"Savings Rate",value:`${(savingsRate*100).toFixed(0)}%`,color:savingsRate>=0.2?"green":savingsRate>=0?"default":"red"},{label:"Burn Rate",value:fC(burnRate)+"/mo",color:"red"},{label:"Debt Coverage",value:debtServiceCoverage!==null?`${debtServiceCoverage.toFixed(1)}x`:"—"},{label:"Cash Runway",value:cashRunway!==null?`${cashRunway.toFixed(0)} mo`:"—"}]}
            ]});
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 18 15 15"/></svg>
            PDF
          </button>
        </div>
        <div className="rep-hero-sub">{range.label}{filterLoc?" · "+locName(locations,filterLoc):""}</div>
        <ReportTabSwitcher activeTab={activeTab} setActiveTab={setActiveTab}/>
        <div className="rep-summary-glass">
          <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:10}}>
            <div style={{fontFamily:"var(--fm)",fontSize:22,fontWeight:600,color:netCashFlow>=0?"var(--grn)":"var(--red)",letterSpacing:"-.02em",lineHeight:1}}>{netCashFlow>=0?"+":"-"}{fmtCompact(Math.abs(netCashFlow))}</div>
            <div style={{fontSize:11,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".06em"}}>{lang==="ar"?"صافي التدفق النقدي":"net cash flow"}</div>
            {fxLoading&&<div style={{fontSize:10,color:"var(--amb)",fontWeight:500,marginInlineStart:4}}>{lang==="ar"?"تحديث الأسعار…":"updating rates…"}</div>}
          </div>
          {(totalCashIn+totalCashOut)>0&&<div style={{display:"flex",height:6,borderRadius:3,overflow:"hidden",marginBottom:12,gap:2}}>
            <div style={{width:`${inBar}%`,background:"var(--grn)",borderRadius:3,minWidth:3}}/>
            <div style={{width:`${outBar}%`,background:"var(--red)",borderRadius:3,minWidth:3}}/>
          </div>}
          <div style={{display:"flex",justifyContent:"space-between",gap:8}}>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:"var(--grn)"}}/>
              <div><div style={{fontSize:10,color:"var(--g400)",fontWeight:500,lineHeight:1}}>{lang==="ar"?"تدفق داخل":"Cash In"}</div><div style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,color:"var(--grn)",lineHeight:1.3}}>{fmtCompact(totalCashIn)}</div></div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:"var(--red)"}}/>
              <div><div style={{fontSize:10,color:"var(--g400)",fontWeight:500,lineHeight:1}}>{lang==="ar"?"تدفق خارج":"Cash Out"}</div><div style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,color:"var(--red)",lineHeight:1.3}}>{fmtCompact(totalCashOut)}</div></div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:"var(--g300)"}}/>
              <div><div style={{fontSize:10,color:"var(--g400)",fontWeight:500,lineHeight:1}}>{lang==="ar"?"تشغيلي":"Operating"}</div><div style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,lineHeight:1.3}}>{netOperating>=0?"+":""}{fmtCompact(netOperating)}</div></div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop: tabs row */}
      <div className="rep-hero-desktop" style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
        <div><ReportTabSwitcher activeTab={activeTab} setActiveTab={setActiveTab}/></div>
      </div>

      {/* Period picker */}
      <ReportPeriodPicker period={period} locations={locations} lang={lang} t={t}/>

      {/* Empty state guidance */}
      {totalOpIn===0&&totalCashOut===0&&(
        <div className="rep-cat-card" style={{padding:24,textAlign:"center",marginBottom:20}}>
          <div style={{fontSize:28,marginBottom:8,opacity:0.3}}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{display:"inline"}}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
          </div>
          <div style={{fontSize:15,fontWeight:600,color:"var(--ink)",marginBottom:6,fontFamily:"var(--fi)"}}>{lang==="ar"?"لا توجد تدفقات نقدية بعد":"No cash flow data yet"}</div>
          <div style={{fontSize:13,color:"var(--g400)",lineHeight:1.5,maxWidth:360,margin:"0 auto"}}>{lang==="ar"?"أضف دخلاً ومصاريف لتتبع التدفقات النقدية التشغيلية، والاستثمارات لتتبع أنشطة الاستثمار.":"Add income and expenses to track operating cash flows, and investments to track investing activities."}</div>
        </div>
      )}

      {/* ═══ Inflows & Outflows (Balance Sheet style) ═══ */}
      <div style={{display:"flex",flexDirection:"column",gap:20,marginBottom:20}}>
        {/* ── Inflows ── */}
        <div style={{display:"flex",flexDirection:"column"}}>
          <div style={{fontSize:11,fontWeight:600,color:"var(--g500)",textTransform:"uppercase",letterSpacing:".08em",marginBottom:10}}>{lang==="ar"?"التدفقات الداخلة":"Inflows"}</div>
          <div style={{flex:1,display:"flex",flexDirection:"column"}}>
            {/* Operating Inflows by category */}
            {inflowCatRows.length>0&&(()=>{const isOpen=!!expandedCF["op-in"];const total=totalOpIn;return(
              <div className="card" style={{marginBottom:12}}>
                <div className="ch" style={{cursor:"pointer"}} onClick={()=>toggleCF("op-in")}>
                  <div style={{display:"flex",alignItems:"center",gap:8,flex:1}}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="var(--grn)" strokeWidth="1.5" strokeLinecap="round" style={{transition:"transform .15s",transform:isOpen?"rotate(90deg)":"rotate(0deg)"}}><path d="M3 1l4 4-4 4"/></svg>
                    <div className="ct">{lang==="ar"?"الدخل التشغيلي":"Operating Income"}</div>
                    <span style={{fontSize:11,color:"var(--g400)",fontWeight:400}}>({inflowCatRows.length})</span>
                  </div>
                  <span style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,color:"var(--grn)"}}>{fmtSAR(total)}</span>
                </div>
                {isOpen&&<div>{inflowCatRows.map(([name,val])=>(
                  <div key={name} style={{display:"flex",alignItems:"center",padding:"9px 16px",borderBottom:"1px solid var(--g100)",gap:8}}>
                    <div style={{flex:1,minWidth:0}}><div style={{fontSize:13,fontWeight:500,letterSpacing:"-.01em"}}>{name}</div></div>
                    <span style={{fontFamily:"var(--fm)",fontSize:12,fontWeight:600,color:"var(--grn)",whiteSpace:"nowrap"}}>{fmtSAR(val)}</span>
                  </div>
                ))}</div>}
              </div>
            );})()}
            {totalOpIn===0&&<div className="card" style={{marginBottom:12}}><div className="empty">{lang==="ar"?"لا توجد تدفقات داخلة":"No inflows in period."}</div></div>}
          </div>
          <div style={{background:"var(--ink)",color:"white",borderRadius:"var(--r2)",padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:13,fontWeight:600}}>{lang==="ar"?"إجمالي الداخل":"Total Inflows"}</span>
            <span style={{fontFamily:"var(--fm)",fontSize:18,fontWeight:400,letterSpacing:"-.02em"}}>{fmtSAR(totalOpIn)}</span>
          </div>
        </div>

        {/* ── Outflows ── */}
        <div style={{display:"flex",flexDirection:"column"}}>
          <div style={{fontSize:11,fontWeight:600,color:"var(--g500)",textTransform:"uppercase",letterSpacing:".08em",marginBottom:10}}>{lang==="ar"?"التدفقات الخارجة":"Outflows"}</div>
          <div style={{flex:1,display:"flex",flexDirection:"column"}}>
            {/* Operating Outflows by category */}
            {outflowCatRows.length>0&&(()=>{const isOpen=!!expandedCF["op-out"];return(
              <div className="card" style={{marginBottom:12}}>
                <div className="ch" style={{cursor:"pointer"}} onClick={()=>toggleCF("op-out")}>
                  <div style={{display:"flex",alignItems:"center",gap:8,flex:1}}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="var(--red)" strokeWidth="1.5" strokeLinecap="round" style={{transition:"transform .15s",transform:isOpen?"rotate(90deg)":"rotate(0deg)"}}><path d="M3 1l4 4-4 4"/></svg>
                    <div className="ct">{lang==="ar"?"المصاريف التشغيلية":"Operating Expenses"}</div>
                    <span style={{fontSize:11,color:"var(--g400)",fontWeight:400}}>({outflowCatRows.length})</span>
                  </div>
                  <span style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,color:"var(--red)"}}>{fmtSAR(totalOpOut)}</span>
                </div>
                {isOpen&&<div>{outflowCatRows.map(([name,val])=>(
                  <div key={name} style={{display:"flex",alignItems:"center",padding:"9px 16px",borderBottom:"1px solid var(--g100)",gap:8}}>
                    <div style={{flex:1,minWidth:0}}><div style={{fontSize:13,fontWeight:500,letterSpacing:"-.01em"}}>{name}</div></div>
                    <span style={{fontFamily:"var(--fm)",fontSize:12,fontWeight:600,color:"var(--red)",whiteSpace:"nowrap"}}>{fmtSAR(val)}</span>
                  </div>
                ))}</div>}
              </div>
            );})()}
            {/* Investing Outflows */}
            {(totalAssetPurchases>0||totalNewInvestments>0)&&(()=>{const isOpen=!!expandedCF["inv-out"];const items=[...assetPurchases.map(e=>({id:e.id,name:e.notes||e.category||"Asset Purchase",amount:e.amount})),...newInvestments.map(h=>({id:h.id,name:h.name,amount:toWsCur(h.invested||0,h.currency)}))];const total=totalAssetPurchases+totalNewInvestments;return(
              <div className="card" style={{marginBottom:12}}>
                <div className="ch" style={{cursor:"pointer"}} onClick={()=>toggleCF("inv-out")}>
                  <div style={{display:"flex",alignItems:"center",gap:8,flex:1}}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="var(--red)" strokeWidth="1.5" strokeLinecap="round" style={{transition:"transform .15s",transform:isOpen?"rotate(90deg)":"rotate(0deg)"}}><path d="M3 1l4 4-4 4"/></svg>
                    <div className="ct">{lang==="ar"?"الاستثمارات":"Investing"}</div>
                    <span style={{fontSize:11,color:"var(--g400)",fontWeight:400}}>({items.length})</span>
                  </div>
                  <span style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,color:"var(--red)"}}>{fmtSAR(total)}</span>
                </div>
                {isOpen&&<div>{items.map(item=>(
                  <div key={item.id} style={{display:"flex",alignItems:"center",padding:"9px 16px",borderBottom:"1px solid var(--g100)",gap:8}}>
                    <div style={{flex:1,minWidth:0}}><div style={{fontSize:13,fontWeight:500,letterSpacing:"-.01em"}}>{item.name}</div></div>
                    <span style={{fontFamily:"var(--fm)",fontSize:12,fontWeight:600,color:"var(--red)",whiteSpace:"nowrap"}}>{fmtSAR(item.amount)}</span>
                  </div>
                ))}</div>}
              </div>
            );})()}
            {/* Financing Outflows */}
            {(periodDebtPayments>0||bulletTotal>0)&&(()=>{const isOpen=!!expandedCF["fin-out"];const items=[...monthlyLiabs.map(l=>({id:l.id,name:l.name,amount:toWsCur(l.monthlyPayment||0,l.currency)*periodMonths,notes:`${fmtSAR(toWsCur(l.monthlyPayment||0,l.currency))}/mo × ${periodMonths}`})),...bulletLiabs.map(l=>({id:l.id,name:l.name,amount:toWsCur(l.outstandingBalance||0,l.currency),notes:"Balloon"}))];const total=periodDebtPayments+bulletTotal;return(
              <div className="card" style={{marginBottom:12}}>
                <div className="ch" style={{cursor:"pointer"}} onClick={()=>toggleCF("fin-out")}>
                  <div style={{display:"flex",alignItems:"center",gap:8,flex:1}}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="var(--red)" strokeWidth="1.5" strokeLinecap="round" style={{transition:"transform .15s",transform:isOpen?"rotate(90deg)":"rotate(0deg)"}}><path d="M3 1l4 4-4 4"/></svg>
                    <div className="ct">{lang==="ar"?"التمويل":"Financing"}</div>
                    <span style={{fontSize:11,color:"var(--g400)",fontWeight:400}}>({items.length})</span>
                  </div>
                  <span style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,color:"var(--red)"}}>{fmtSAR(total)}</span>
                </div>
                {isOpen&&<div>{items.map(item=>(
                  <div key={item.id} style={{display:"flex",alignItems:"center",padding:"9px 16px",borderBottom:"1px solid var(--g100)",gap:8}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:500,letterSpacing:"-.01em"}}>{item.name}</div>
                      {item.notes&&<div style={{fontSize:11,color:"var(--g400)"}}>{item.notes}</div>}
                    </div>
                    <span style={{fontFamily:"var(--fm)",fontSize:12,fontWeight:600,color:"var(--red)",whiteSpace:"nowrap"}}>{fmtSAR(item.amount)}</span>
                  </div>
                ))}</div>}
              </div>
            );})()}
            {totalOpOut===0&&totalAssetPurchases===0&&totalNewInvestments===0&&periodDebtPayments===0&&bulletTotal===0&&<div className="card" style={{marginBottom:12}}><div className="empty">{lang==="ar"?"لا توجد تدفقات خارجة":"No outflows in period."}</div></div>}
          </div>
          <div style={{background:totalCashOut>0?"var(--ink)":"var(--g200)",color:"white",borderRadius:"var(--r2)",padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:13,fontWeight:600}}>{lang==="ar"?"إجمالي الخارج":"Total Outflows"}</span>
            <span style={{fontFamily:"var(--fm)",fontSize:18,fontWeight:400,letterSpacing:"-.02em"}}>{fmtSAR(totalCashOut)}</span>
          </div>
        </div>
      </div>

      {/* ═══ Net Cash Flow ═══ */}
      <div style={{marginBottom:20}}>
        <div style={{fontSize:11,fontWeight:600,color:"var(--g500)",textTransform:"uppercase",letterSpacing:".08em",marginBottom:10}}>{lang==="ar"?"صافي التدفق النقدي":"Net Cash Flow"}</div>
        <div className="card" style={{marginBottom:12}}>
          <div style={{display:"flex",justifyContent:"space-between",padding:"10px 18px",borderBottom:"1px solid var(--g100)"}}><span style={{fontSize:13,color:"var(--g600)"}}>{lang==="ar"?"إجمالي الداخل":"Total Inflows"}</span><span style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,color:"var(--grn)"}}>+{fmtSAR(totalOpIn)}</span></div>
          <div style={{display:"flex",justifyContent:"space-between",padding:"10px 18px"}}><span style={{fontSize:13,color:"var(--g600)"}}>{lang==="ar"?"إجمالي الخارج":"Total Outflows"}</span><span style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,color:"var(--red)"}}>-{fmtSAR(totalCashOut)}</span></div>
        </div>
        <div style={{background:netCashFlow>=0?"var(--grn)":"var(--red)",color:"white",borderRadius:"var(--r2)",padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:13,fontWeight:600}}>{lang==="ar"?"صافي التدفق النقدي":"Net Cash Flow"}</span>
          <span style={{fontFamily:"var(--fm)",fontSize:18,fontWeight:400,letterSpacing:"-.02em"}}>{netCashFlow>=0?"+":""}{fmtSAR(netCashFlow)}</span>
        </div>
      </div>

      {/* ── Visual Charts ── */}
      {(inflowCatRows.length>0||outflowCatRows.length>0)&&(()=>{
        const chartColors=reportChartColors(state.branding?.anchorColor);
        return(
          <div style={{marginBottom:20}}>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:16,marginBottom:16}}>
              {inflowCatRows.length>0&&(
                <div className="rep-chart-card">
                  <div className="rep-cat-title">{lang==="ar"?"التدفقات الداخلة حسب الفئة":"Cash Inflows by Category"}</div>
                  <ReportDonutChart data={inflowCatRows} colors={chartColors} total={totalOpIn} label={lang==="ar"?"إجمالي الداخل":"Total Inflows"} totalColor="var(--grn)"/>
                  <ReportLegend data={inflowCatRows} colors={chartColors} total={totalOpIn}/>
                </div>
              )}
              {outflowCatRows.length>0&&(
                <div className="rep-chart-card">
                  <div className="rep-cat-title">{lang==="ar"?"التدفقات الخارجة حسب الفئة":"Cash Outflows by Category"}</div>
                  <ReportDonutChart data={outflowCatRows} colors={chartColors} total={totalOpOut} label={lang==="ar"?"إجمالي الخارج":"Total Outflows"} totalColor="var(--red)"/>
                  <ReportLegend data={outflowCatRows} colors={chartColors} total={totalOpOut}/>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ═══ Recurring Cash Flow Projection ═══ */}
      <div className="card" style={{marginBottom:16,borderRadius:16,overflow:"hidden"}}>
        <div style={{padding:"16px 24px"}}><div style={{fontSize:15,fontWeight:600,color:"var(--ink)",letterSpacing:"-.01em",fontFamily:"var(--fi)"}}>{lang==="ar"?"توقعات التدفق النقدي المتكرر":"Recurring Cash Flow Projection"}</div></div>
        <div style={{padding:"0 24px 24px"}}>
          <div style={{display:"flex",flexWrap:"wrap",gap:16,marginBottom:12}}>
            {monthlyRecurInc>0&&<div className="stat" style={{flex:1,minWidth:120}}><div className="stat-l">{lang==="ar"?"الدخل الشهري":"Monthly Income"}</div><div className="stat-v" style={{fontSize:16,color:"var(--grn)"}}>{fmtCompact(monthlyRecurInc)}</div></div>}
            <div className="stat" style={{flex:1,minWidth:120}}><div className="stat-l">{lang==="ar"?"المصاريف الشهرية":"Monthly Expenses"}</div><div className="stat-v" style={{fontSize:16,color:"var(--red)"}}>{fmtCompact(monthlyRecurExp)}</div></div>
            {monthlyDebtService>0&&<div className="stat" style={{flex:1,minWidth:120}}><div className="stat-l">{lang==="ar"?"خدمة الدين الشهرية":"Monthly Debt Service"}</div><div className="stat-v" style={{fontSize:16,color:"var(--red)"}}>{fmtCompact(monthlyDebtService)}</div><div className="stat-s">{monthlyLiabs.length} {lang==="ar"?"قسط":"amortising"}</div></div>}
            <div className="stat" style={{flex:1,minWidth:120}}><div className="stat-l">{lang==="ar"?"صافي شهري":"Net Monthly"}</div><div className="stat-v" style={{fontSize:16,color:netMonthlyRecur>=0?"var(--grn)":"var(--red)"}}>{netMonthlyRecur>=0?"+":""}{fmtCompact(netMonthlyRecur)}</div></div>
            <div className="stat" style={{flex:1,minWidth:120}}><div className="stat-l">{lang==="ar"?"التوقع السنوي":"Annual Projection"}</div><div className="stat-v" style={{fontSize:16,color:netAnnualProjection>=0?"var(--grn)":"var(--red)"}}>{netAnnualProjection>=0?"+":""}{fmtCompact(netAnnualProjection)}</div></div>
          </div>
          <div style={{fontSize:12,color:"var(--g400)"}}>{(recurringIncome||[]).filter(r=>r.active).length} {lang==="ar"?"مصدر دخل":"income sources"} · {(recurringExpenses||[]).filter(r=>r.active).length} {lang==="ar"?"جدول مصاريف":"expense schedules"} · {monthlyLiabs.length} {lang==="ar"?"قسط":"amortising"} · {bulletLiabs.length} {lang==="ar"?"دفعة كبيرة":"balloon"}</div>
        </div>
      </div>

      {/* ═══ Health indicators — 2x2 grid ═══ */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
        <div className="card" style={{padding:"16px 18px",textAlign:"center"}}>
          <div style={{fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".07em",marginBottom:8}}>{lang==="ar"?"معدل الادخار":"Savings Rate"}</div>
          <div style={{position:"relative",width:72,height:72,margin:"0 auto 8px"}}>
            <svg viewBox="0 0 36 36" style={{width:72,height:72,transform:"rotate(-90deg)"}}>
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="var(--g100)" strokeWidth="3"/>
              <circle cx="18" cy="18" r="15.9" fill="none" stroke={savingsRate>=0.2?"var(--grn)":savingsRate>=0?"var(--amb)":"var(--red)"} strokeWidth="3" strokeDasharray={`${Math.max(Math.min(Math.abs(savingsRate)*100,100),0)} 100`} strokeLinecap="round"/>
            </svg>
            <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"var(--fm)",fontSize:14,fontWeight:700,color:savingsRate>=0.2?"var(--grn)":savingsRate>=0?"var(--amb)":"var(--red)"}}>{(savingsRate*100).toFixed(0)}%</div>
          </div>
          <div style={{fontSize:11,color:"var(--g500)"}}>{savingsRate>=0.2?(lang==="ar"?"قوي":"Strong"):savingsRate>=0?(lang==="ar"?"منخفض":"Low"):(lang==="ar"?"سلبي":"Negative")}</div>
        </div>
        <div className="card" style={{padding:"16px 18px",textAlign:"center"}}>
          <div style={{fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".07em",marginBottom:8}}>{lang==="ar"?"معدل الحرق":"Burn Rate"}</div>
          <div style={{fontFamily:"var(--fm)",fontSize:22,fontWeight:400,color:"var(--red)",margin:"16px 0 8px"}}>{fmtCompact(burnRate)}</div>
          <div style={{fontSize:11,color:"var(--g500)"}}>{lang==="ar"?"شهرياً":"per month"}</div>
        </div>
        <div className="card" style={{padding:"16px 18px",textAlign:"center"}}>
          <div style={{fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".07em",marginBottom:8}}>{lang==="ar"?"تغطية الدين":"Debt Coverage"}</div>
          <div style={{fontFamily:"var(--fm)",fontSize:28,fontWeight:400,color:debtServiceCoverage!==null&&debtServiceCoverage>=1.5?"var(--grn)":debtServiceCoverage!==null&&debtServiceCoverage>=1?"var(--amb)":debtServiceCoverage!==null?"var(--red)":"var(--g400)",margin:"12px 0"}}>{debtServiceCoverage!==null?`${debtServiceCoverage.toFixed(1)}x`:"—"}</div>
          <div style={{fontSize:11,color:"var(--g500)"}}>{debtServiceCoverage===null?(lang==="ar"?"لا يوجد دين":"No debt"):debtServiceCoverage>=1.5?(lang==="ar"?"قوي":"Strong"):debtServiceCoverage>=1?(lang==="ar"?"كافي":"Adequate"):(lang==="ar"?"معرض للخطر":"At Risk")}</div>
        </div>
        <div className="card" style={{padding:"16px 18px",textAlign:"center"}}>
          <div style={{fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".07em",marginBottom:8}}>{lang==="ar"?"المدرج النقدي":"Cash Runway"}</div>
          <div style={{fontFamily:"var(--fm)",fontSize:28,fontWeight:400,color:cashRunway!==null&&cashRunway>=12?"var(--grn)":cashRunway!==null&&cashRunway>=6?"var(--amb)":cashRunway!==null?"var(--red)":"var(--g400)",margin:"12px 0"}}>{cashRunway!==null?cashRunway.toFixed(0):"—"}<span style={{fontSize:14}}> {cashRunway!==null?(lang==="ar"?"شهر":"mo"):""}</span></div>
          <div style={{fontSize:11,color:"var(--g500)"}}>{cashRunway===null?"—":cashRunway>=12?(lang==="ar"?"مريح":"Comfortable"):cashRunway>=6?(lang==="ar"?"معتدل":"Moderate"):(lang==="ar"?"ضيق":"Tight")}</div>
          {!hasLiquidityCfg&&cashRunway!==null&&<div style={{fontSize:9,color:"var(--amb)",marginTop:4}}>{lang==="ar"?"يشمل جميع الحيازات (لم يتم تعيين السيولة)":"Includes all holdings (liquidity not configured)"}</div>}
        </div>
      </div>

      <div className="rep-ai-wrap" style={{marginTop:8,marginBottom:0}}><AIPanel title={lang==="ar"?"ذكاء التدفق النقدي":"Cash Flow Intelligence"} context={`Period: ${range.label}. Operating: Inflows ${fmtSAR(totalOpIn)}, Outflows ${fmtSAR(totalOpOut)}, Net ${fmtSAR(netOperating)}. Investing: Asset purchases ${fmtSAR(totalAssetPurchases)}, New investments ${fmtSAR(totalNewInvestments)}, Net ${fmtSAR(netInvesting)}. Financing: Debt payments ${fmtSAR(periodDebtPayments)}, Net ${fmtSAR(netFinancing)}. Net Cash Flow: ${fmtSAR(netCashFlow)}. Savings rate: ${(savingsRate*100).toFixed(1)}%. Burn rate: ${fmtSAR(burnRate)}/mo. ${debtServiceCoverage!==null?`Debt coverage: ${debtServiceCoverage.toFixed(1)}x. `:""}${cashRunway!==null?`Cash runway: ${cashRunway.toFixed(0)} months. `:""}Monthly recurring: In ${fmtSAR(monthlyRecurInc)}, Out ${fmtSAR(monthlyRecurExp)}, Debt ${fmtSAR(monthlyDebtService)}, Net ${fmtSAR(netMonthlyRecur)}. Top inflow cats: ${inflowCatRows.slice(0,5).map(([n,v])=>`${n} ${fmtSAR(v)}`).join(", ")}. Top outflow cats: ${outflowCatRows.slice(0,5).map(([n,v])=>`${n} ${fmtSAR(v)}`).join(", ")}`} prompt={lang==="ar"?"حلل صحة التدفق النقدي. هل التدفق مستدام؟ مخاطر رئيسية؟":"Analyze cash flow health. Is cash flow sustainable? Key inflow/outflow risks? 3 insights."} lang={lang} moduleKey="Reports" wsState={state}/></div>

    </div>
  );
}

export default ReportsModule;
export { generateReportPDF, getDateRange, inRange, reportChartColors, ReportDonutChart, ReportLegend, REPORT_MONTHS, REPORT_YEARS, useReportPeriod, ReportPeriodPicker, useFxRates, ReportTabSwitcher, PnLModule, BalanceSheetModule, AddBalanceItemModal, CashFlowModule };
